/**
 * Elite Batch 8 Part 2 — Workflow Templates 668–690
 * Finance, accounting, subscription management, procurement, and compliance workflows.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendSMS",
    label,
    y,
    { sendSMSDetails: { to, content: body } },
    x,
  );
}
function stripeNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://api.stripe.com/v1/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {stripe_secret_key}" },
          { key: "Content-Type", value: "application/x-www-form-urlencoded" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function hubspotNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function quickbooksNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://quickbooks.api.intuit.com/v3/company/{company_id}/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {quickbooks_token}" },
          { key: "Content-Type", value: "application/json" },
          { key: "Accept", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 8 PART 2 — TEMPLATES 668–690
// =============================================================================

export const ELITE_BATCH8_PART2: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 668. STRIPE EVENT TO ACCOUNTING SYNC (8 nodes)
  // ===================================================================

  "Stripe Event to Accounting Sync": {
    summary:
      "Synchronizes Stripe financial events to QuickBooks journal entries in real time. A Stripe webhook triggers event type routing via runCode, maps the event to a journal template, checks idempotency via condition, creates a QuickBooks journal entry, handles refund/dispute reversals, logs to Google Sheets, and sends an accounting Slack alert.",
    nodes: [
      trigger("Webhook: Stripe financial event", "webhook_app", {
        source: "Stripe",
        event: "charge.succeeded | charge.refunded | charge.dispute.created",
        howItStarts:
          "Stripe fires a webhook on any charge, refund, or dispute event, initiating accounting synchronization",
        setup:
          "Configure Stripe webhooks for charge.succeeded, charge.refunded, and charge.dispute.created events. Ensure QuickBooks API credentials and company ID are configured.",
        inputFields: [],
      }),

      // 2. Event type routing (runCode)
      n("2", "runCode", "Route event type & extract fields", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `const payload = JSON.parse(vars.webhook_payload || "{}");
const data = payload.data ? payload.data.object : payload;
const event_type = payload.type || "unknown";
const charge_id = data.id || "";
const amount = ((data.amount || 0) / 100).toFixed(2);
const currency = (data.currency || "usd").toUpperCase();
const customer_id = data.customer || "";
const description = data.description || data.statement_descriptor || "Stripe charge";
const is_refund = event_type.includes("refund");
const is_dispute = event_type.includes("dispute");
const journal_type = is_dispute ? "dispute_reversal" : is_refund ? "refund_reversal" : "revenue_entry";
const debit_account = is_refund || is_dispute ? "Revenue" : "Accounts Receivable";
const credit_account = is_refund || is_dispute ? "Accounts Receivable" : "Revenue";
const idempotency_key = event_type + "_" + charge_id;
return { event_type, charge_id, amount, currency, customer_id, description, journal_type, debit_account, credit_account, idempotency_key, is_reversal: (is_refund || is_dispute) ? "yes" : "no" };`,
          outputVariables: [
            { name: "event_type", type: "string" },
            { name: "charge_id", type: "string" },
            { name: "amount", type: "string" },
            { name: "currency", type: "string" },
            { name: "customer_id", type: "string" },
            { name: "description", type: "string" },
            { name: "journal_type", type: "string" },
            { name: "debit_account", type: "string" },
            { name: "credit_account", type: "string" },
            { name: "idempotency_key", type: "string" },
            { name: "is_reversal", type: "string" },
          ],
        },
      }),

      // 3. Journal template mapping (AI)
      aiNode(
        "3",
        "AI: Map to journal template",
        520,
        "You are an accounting automation specialist. Given a Stripe event, generate the journal entry details following double-entry bookkeeping. Map the charge to the correct revenue account, consider taxes and fees, and produce the journal memo. Respond JSON: {journal_memo: string, line_items: string, tax_amount: string, net_amount: string, account_code: string}",
        "Event: {event_type}. Amount: {amount} {currency}. Description: {description}. Journal type: {journal_type}. Debit: {debit_account}. Credit: {credit_account}. Charge ID: {charge_id}.",
        [
          { field: "journal_memo", as: "journal_memo", type: "string" },
          { field: "line_items", as: "journal_line_items", type: "string" },
          { field: "tax_amount", as: "tax_amount", type: "string" },
          { field: "net_amount", as: "net_amount", type: "string" },
          { field: "account_code", as: "account_code", type: "string" },
        ],
      ),

      // 4. Idempotency check condition
      n("4", "condition", "Already synced (idempotency)?", 760, {
        conditionData: [
          {
            variable: "idempotency_key",
            value: "",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. QuickBooks journal creation
      quickbooksNode(
        "5",
        "Create QuickBooks journal entry",
        1000,
        "POST",
        "journalentry",
        [
          { key: "Line", value: "{journal_line_items}" },
          { key: "TxnDate", value: new Date().toISOString().split("T")[0] },
          { key: "PrivateNote", value: "{journal_memo} | Stripe: {charge_id}" },
          { key: "DocNumber", value: "{idempotency_key}" },
        ],
        [
          {
            name: "JournalEntry.Id",
            customName: "qb_journal_id",
            type: "string",
          },
          {
            name: "JournalEntry.TxnDate",
            customName: "qb_txn_date",
            type: "string",
          },
        ],
      ),

      // 6. Refund/dispute reversal entries
      n("6", "runCode", "Process reversal entries if applicable", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `const isReversal = vars.is_reversal === "yes";
const reversalNote = isReversal
  ? "REVERSAL: " + vars.journal_type + " for charge " + vars.charge_id + ". Original amount: $" + vars.amount + " " + vars.currency + ". QB Journal: " + vars.qb_journal_id
  : "Standard entry for charge " + vars.charge_id + ". Amount: $" + vars.amount + " " + vars.currency + ". QB Journal: " + vars.qb_journal_id;
const sync_status = vars.qb_journal_id ? "synced" : "failed";
const timestamp = new Date().toISOString();
return { reversal_note: reversalNote, sync_status, sync_timestamp: timestamp };`,
          outputVariables: [
            { name: "reversal_note", type: "string" },
            { name: "sync_status", type: "string" },
            { name: "sync_timestamp", type: "string" },
          ],
        },
      }),

      // 7. Sync logging (Sheets)
      sheetsNode(
        "7",
        "Log sync to Sheets",
        1480,
        "AccountingSync",
        '[["{sync_timestamp}","{event_type}","{charge_id}","{amount}","{currency}","{journal_type}","{qb_journal_id}","{sync_status}","{reversal_note}"]]',
      ),

      // 8. Accounting Slack alert
      slackNode(
        "8",
        "Slack: Accounting sync alert",
        1720,
        ":ledger: *Accounting Sync*\\nEvent: {event_type}\\nCharge: {charge_id}\\nAmount: ${amount} {currency}\\nJournal: {qb_journal_id}\\nStatus: {sync_status}\\n{reversal_note}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // new event -> create journal
      e("4", "7", "output_2"), // duplicate -> skip to logging
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 669. STRIPE PAYMENT TO CRM DEAL UPDATER (8 nodes)
  // ===================================================================

  "Stripe Payment to CRM Deal Updater": {
    summary:
      "Updates HubSpot CRM deals when Stripe payments succeed. A Stripe payment_intent.succeeded webhook triggers customer resolution, checks HubSpot contact match via condition, creates new contacts if needed, updates deal stage to closed-won, enriches with MRR/LTV data, posts a sales win to Slack, and logs attribution to Sheets.",
    nodes: [
      trigger("Webhook: Stripe payment succeeded", "webhook_app", {
        source: "Stripe",
        event: "payment_intent.succeeded",
        howItStarts:
          "Stripe fires a webhook when a payment intent succeeds, triggering CRM synchronization and deal closure",
        setup:
          "Configure Stripe webhook for payment_intent.succeeded. Ensure HubSpot API token has contacts and deals write permissions.",
        inputFields: [],
      }),

      // 2. Customer resolution (runCode)
      n("2", "runCode", "Resolve customer from Stripe payload", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `const payload = JSON.parse(vars.webhook_payload || "{}");
const data = payload.data ? payload.data.object : payload;
const customer_email = (data.receipt_email || data.charges?.data?.[0]?.billing_details?.email || data.customer_email || "").toLowerCase();
const customer_name = data.charges?.data?.[0]?.billing_details?.name || data.customer_name || "";
const amount = ((data.amount || data.amount_received || 0) / 100).toFixed(2);
const currency = (data.currency || "usd").toUpperCase();
const payment_id = data.id || "";
const stripe_customer_id = data.customer || "";
const description = data.description || "Payment";
const payment_method = data.payment_method_types?.[0] || "card";
return { customer_email, customer_name, amount, currency, payment_id, stripe_customer_id, description, payment_method };`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "amount", type: "string" },
            { name: "currency", type: "string" },
            { name: "payment_id", type: "string" },
            { name: "stripe_customer_id", type: "string" },
            { name: "description", type: "string" },
            { name: "payment_method", type: "string" },
          ],
        },
      }),

      // 3. HubSpot contact match condition
      hubspotNode(
        "3",
        "Search HubSpot contact by email",
        520,
        "POST",
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{customer_email}"}]}]',
          },
        ],
        [
          { name: "total", customName: "hs_contact_count", type: "number" },
          {
            name: "results[0].id",
            customName: "hs_contact_id",
            type: "string",
          },
        ],
      ),

      // 4. Condition: contact found?
      n("4", "condition", "HubSpot contact exists?", 760, {
        conditionData: [
          {
            variable: "hs_contact_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. New contact creation (flagged for review) — false branch merges back
      hubspotNode(
        "5",
        "Create HubSpot contact (flagged)",
        1000,
        "POST",
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{customer_email}","firstname":"{customer_name}","lifecyclestage":"customer","hs_lead_status":"OPEN","stripe_customer_id":"{stripe_customer_id}","notes":"Auto-created from Stripe payment {payment_id} — flagged for sales review"}',
          },
        ],
        [{ name: "id", customName: "hs_contact_id", type: "string" }],
        300,
      ),

      // 6. Deal stage update to closed-won + MRR/LTV enrichment
      hubspotNode(
        "6",
        "Update deal to closed-won + MRR enrichment",
        1240,
        "POST",
        "crm/v3/objects/deals",
        [
          {
            key: "properties",
            value:
              '{"dealname":"{customer_name} - {description}","dealstage":"closedwon","amount":"{amount}","pipeline":"default","closedate":"' +
              new Date().toISOString().split("T")[0] +
              '","stripe_payment_id":"{payment_id}","payment_method":"{payment_method}","currency":"{currency}"}',
          },
          {
            key: "associations",
            value:
              '[{"to":{"id":"{hs_contact_id}"},"types":[{"associationCategory":"HUBSPOT_DEFINED","associationTypeId":3}]}]',
          },
        ],
        [
          { name: "id", customName: "hs_deal_id", type: "string" },
          {
            name: "properties.amount",
            customName: "deal_amount",
            type: "string",
          },
        ],
      ),

      // 7. Sales win Slack post
      slackNode(
        "7",
        "Slack: Sales win announcement",
        1480,
        ":tada: *Deal Closed!*\\nCustomer: {customer_name} ({customer_email})\\nAmount: ${amount} {currency}\\nPayment: {payment_id}\\nDeal: {hs_deal_id}\\nMethod: {payment_method}",
      ),

      // 8. Attribution tagging (Sheets)
      sheetsNode(
        "8",
        "Log attribution to Sheets",
        1720,
        "PaymentAttribution",
        '[["{customer_email}","{customer_name}","{amount}","{currency}","{payment_id}","{hs_contact_id}","{hs_deal_id}","{payment_method}","' +
          new Date().toISOString().split("T")[0] +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "6", "output_1"), // contact exists -> update deal
      e("4", "5", "output_2"), // no contact -> create first
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 670. STRIPE PAYMENT TO GOOGLE SHEET LOGGER (7 nodes)
  // ===================================================================

  "Stripe Payment to Google Sheet Logger": {
    summary:
      "Logs every Stripe charge and refund to a normalized Google Sheet for audit and reconciliation. A Stripe webhook triggers field extraction via runCode, checks for duplicates via condition, appends a normalized row to Sheets, updates running totals, sends a failure Slack alert on errors, and logs audit records.",
    nodes: [
      trigger("Webhook: Stripe charge/refund", "webhook_app", {
        source: "Stripe",
        event: "charge.succeeded | charge.refunded",
        howItStarts:
          "Stripe fires a webhook on charge or refund events, triggering structured logging to Google Sheets",
        setup:
          "Configure Stripe webhooks for charge.succeeded and charge.refunded. Create a Google Sheet with columns: Timestamp, Type, Charge ID, Amount, Currency, Customer, Description, Status.",
        inputFields: [],
      }),

      // 2. Field extraction (runCode)
      n("2", "runCode", "Extract & normalize Stripe fields", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `const payload = JSON.parse(vars.webhook_payload || "{}");
const data = payload.data ? payload.data.object : payload;
const event_type = payload.type || "unknown";
const charge_id = data.id || "";
const amount = ((data.amount || 0) / 100).toFixed(2);
const currency = (data.currency || "usd").toUpperCase();
const customer_email = data.billing_details?.email || data.receipt_email || "";
const customer_name = data.billing_details?.name || "";
const description = data.description || data.statement_descriptor || "N/A";
const status = data.status || "unknown";
const is_refund = event_type.includes("refund") ? "refund" : "charge";
const timestamp = new Date().toISOString();
const row_key = charge_id + "_" + is_refund;
return { event_type, charge_id, amount, currency, customer_email, customer_name, description, status, is_refund, timestamp, row_key };`,
          outputVariables: [
            { name: "event_type", type: "string" },
            { name: "charge_id", type: "string" },
            { name: "amount", type: "string" },
            { name: "currency", type: "string" },
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "description", type: "string" },
            { name: "status", type: "string" },
            { name: "is_refund", type: "string" },
            { name: "timestamp", type: "string" },
            { name: "row_key", type: "string" },
          ],
        },
      }),

      // 3. Duplicate check condition
      n("3", "condition", "Valid charge ID present?", 520, {
        conditionData: [
          {
            variable: "charge_id",
            value: "",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Normalized row append (Sheets)
      sheetsNode(
        "4",
        "Append normalized row to Sheets",
        760,
        "StripePayments",
        '[["{timestamp}","{is_refund}","{charge_id}","{amount}","{currency}","{customer_email}","{customer_name}","{description}","{status}"]]',
      ),

      // 5. Running totals update (runCode)
      n("5", "runCode", "Compute running totals", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `const amount = parseFloat(vars.amount) || 0;
const isRefund = vars.is_refund === "refund";
const netAmount = isRefund ? -amount : amount;
const logSuccess = parseInt(vars.rows_logged) || 0;
const writeStatus = logSuccess > 0 ? "logged" : "failed";
return { net_amount: netAmount.toFixed(2), write_status: writeStatus, log_summary: vars.is_refund + " of $" + vars.amount + " " + vars.currency + " for " + vars.customer_email + " — " + writeStatus };`,
          outputVariables: [
            { name: "net_amount", type: "string" },
            { name: "write_status", type: "string" },
            { name: "log_summary", type: "string" },
          ],
        },
      }),

      // 6. Failure Slack alert (only on write failure)
      slackNode(
        "6",
        "Slack: Log failure alert",
        1240,
        ":warning: *Stripe Sheet Log Failure*\\nCharge: {charge_id}\\nType: {is_refund}\\nAmount: ${amount} {currency}\\nCustomer: {customer_email}\\nStatus: {write_status}\\nPlease check the Google Sheets integration.",
      ),

      // 7. Audit logging
      sheetsNode(
        "7",
        "Append audit log entry",
        1480,
        "AuditLog",
        '[["{timestamp}","{event_type}","{charge_id}","{write_status}","{log_summary}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // valid charge -> append row
      e("3", "6", "output_2"), // invalid -> alert
      e("4", "5"),
      e("5", "7"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 671. STRIPE REVENUE TO GOOGLE SHEET TRACKER (8 nodes)
  // ===================================================================

  "Stripe Revenue to Google Sheet Tracker": {
    summary:
      "Tracks hourly Stripe revenue metrics with anomaly detection. An hourly schedule triggers a Stripe balance transactions pull, classifies transactions via runCode, computes hourly revenue metrics, appends a snapshot to Sheets, checks anomaly thresholds via condition, sends a Slack alert on anomalies, and delivers an end-of-day email summary.",
    nodes: [
      trigger("Schedule: Hourly revenue pull", "schedule", {
        schedule: "0 * * * *",
        howItStarts:
          "Runs every hour to pull the latest Stripe balance transactions and compute revenue metrics",
        setup:
          "Set schedule to run every hour. Configure Stripe API key. Create Google Sheet with tabs: HourlyRevenue and DailyRevenue.",
        inputFields: [],
      }),

      // 2. Stripe balance transactions pull
      stripeNode(
        "2",
        "Pull Stripe balance transactions",
        280,
        "GET",
        "balance_transactions?limit=100&created[gte]={hour_start_unix}&type=charge",
        [],
        [
          { name: "data", customName: "transactions_json", type: "string" },
          { name: "has_more", customName: "has_more", type: "boolean" },
        ],
      ),

      // 3. Transaction classification (runCode)
      n("3", "runCode", "Classify transactions & compute metrics", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `const txns = JSON.parse(vars.transactions_json || "[]");
let totalRevenue = 0;
let totalFees = 0;
let txnCount = 0;
let refundTotal = 0;
const byCurrency = {};
for (const t of txns) {
  const amt = (t.amount || 0) / 100;
  const fee = (t.fee || 0) / 100;
  const cur = (t.currency || "usd").toUpperCase();
  if (t.type === "charge") { totalRevenue += amt; totalFees += fee; txnCount++; }
  if (t.type === "refund") { refundTotal += Math.abs(amt); }
  byCurrency[cur] = (byCurrency[cur] || 0) + amt;
}
const netRevenue = totalRevenue - refundTotal - totalFees;
const avgTicket = txnCount > 0 ? (totalRevenue / txnCount).toFixed(2) : "0.00";
const currencyBreakdown = Object.entries(byCurrency).map(([k,v]) => k + ": $" + Number(v).toFixed(2)).join(", ");
const hour = new Date().getHours();
const anomalyThreshold = 500;
const isAnomaly = netRevenue > anomalyThreshold * 3 || (hour > 6 && hour < 22 && netRevenue < anomalyThreshold * 0.1) ? "yes" : "no";
return { total_revenue: totalRevenue.toFixed(2), total_fees: totalFees.toFixed(2), refund_total: refundTotal.toFixed(2), net_revenue: netRevenue.toFixed(2), txn_count: String(txnCount), avg_ticket: avgTicket, currency_breakdown: currencyBreakdown, is_anomaly: isAnomaly, hour_label: String(hour) + ":00" };`,
          outputVariables: [
            { name: "total_revenue", type: "string" },
            { name: "total_fees", type: "string" },
            { name: "refund_total", type: "string" },
            { name: "net_revenue", type: "string" },
            { name: "txn_count", type: "string" },
            { name: "avg_ticket", type: "string" },
            { name: "currency_breakdown", type: "string" },
            { name: "is_anomaly", type: "string" },
            { name: "hour_label", type: "string" },
          ],
        },
      }),

      // 4. Sheet snapshot append
      sheetsNode(
        "4",
        "Append hourly snapshot to Sheets",
        760,
        "HourlyRevenue",
        '[["{hour_label}","' +
          new Date().toISOString().split("T")[0] +
          '","{total_revenue}","{total_fees}","{refund_total}","{net_revenue}","{txn_count}","{avg_ticket}","{currency_breakdown}"]]',
      ),

      // 5. Anomaly threshold condition
      n("5", "condition", "Revenue anomaly detected?", 1000, {
        conditionData: [
          {
            variable: "is_anomaly",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Slack alert on anomaly
      slackNode(
        "6",
        "Slack: Revenue anomaly alert",
        1240,
        ":rotating_light: *Revenue Anomaly Detected*\\nHour: {hour_label}\\nNet Revenue: ${net_revenue}\\nTransactions: {txn_count}\\nAvg Ticket: ${avg_ticket}\\nRefunds: ${refund_total}\\nBreakdown: {currency_breakdown}\\nPlease investigate immediately.",
      ),

      // 7. End-of-day email summary (sent every hour, filtered in email system for EOD)
      emailNode(
        "7",
        "Email: Hourly revenue digest",
        1480,
        "{finance_team_email}",
        "Stripe Revenue: {hour_label} — Net ${net_revenue} ({txn_count} txns)",
        "Hourly Revenue Report\\n\\nHour: {hour_label}\\nGross Revenue: ${total_revenue}\\nFees: ${total_fees}\\nRefunds: ${refund_total}\\nNet Revenue: ${net_revenue}\\nTransactions: {txn_count}\\nAvg Ticket: ${avg_ticket}\\n\\nCurrency Breakdown: {currency_breakdown}\\n\\nAnomaly Status: {is_anomaly}",
      ),

      // 8. Daily aggregation logging
      sheetsNode(
        "8",
        "Log daily aggregation checkpoint",
        1720,
        "DailyRevenue",
        '[["{hour_label}","{net_revenue}","{txn_count}","{is_anomaly}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // anomaly -> Slack
      e("5", "7", "output_2"), // normal -> email summary
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 672. STRIPE SLACK PAYMENT ALERTS (7 nodes)
  // ===================================================================

  "Stripe Slack Payment Alerts": {
    summary:
      "Sends rich Slack alerts for every Stripe payment with severity classification and escalation. A Stripe webhook triggers severity classification via runCode, enriches the customer via HubSpot, routes on success/failure via condition, sends a Slack Block Kit alert, waits for dispute SLA via delay, and escalates if unacknowledged.",
    nodes: [
      trigger("Webhook: Stripe payment event", "webhook_app", {
        source: "Stripe",
        event: "charge.succeeded | charge.failed | charge.dispute.created",
        howItStarts:
          "Stripe fires a webhook on any charge event (success, failure, dispute), initiating payment alerting",
        setup:
          "Configure Stripe webhooks for charge.succeeded, charge.failed, and charge.dispute.created. Set up Slack incoming webhook URL.",
        inputFields: [],
      }),

      // 2. Severity classification (runCode)
      n("2", "runCode", "Classify payment severity", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `const payload = JSON.parse(vars.webhook_payload || "{}");
const data = payload.data ? payload.data.object : payload;
const event_type = payload.type || "unknown";
const charge_id = data.id || "";
const amount = ((data.amount || 0) / 100).toFixed(2);
const currency = (data.currency || "usd").toUpperCase();
const customer_email = data.billing_details?.email || data.receipt_email || "";
const customer_id = data.customer || "";
const status = data.status || "unknown";
const failure_code = data.failure_code || "";
const failure_message = data.failure_message || "";
const is_dispute = event_type.includes("dispute");
const is_failure = status === "failed" || event_type.includes("failed");
const amtNum = parseFloat(amount);
let severity = "info";
if (is_dispute) severity = "critical";
else if (is_failure && amtNum > 100) severity = "high";
else if (is_failure) severity = "medium";
else if (amtNum > 500) severity = "medium";
const is_success = !is_failure && !is_dispute ? "yes" : "no";
return { event_type, charge_id, amount, currency, customer_email, customer_id, status, failure_code, failure_message, severity, is_success, is_dispute: is_dispute ? "yes" : "no" };`,
          outputVariables: [
            { name: "event_type", type: "string" },
            { name: "charge_id", type: "string" },
            { name: "amount", type: "string" },
            { name: "currency", type: "string" },
            { name: "customer_email", type: "string" },
            { name: "customer_id", type: "string" },
            { name: "status", type: "string" },
            { name: "failure_code", type: "string" },
            { name: "failure_message", type: "string" },
            { name: "severity", type: "string" },
            { name: "is_success", type: "string" },
            { name: "is_dispute", type: "string" },
          ],
        },
      }),

      // 3. Customer enrichment (HubSpot)
      hubspotNode(
        "3",
        "Enrich customer from HubSpot",
        520,
        "POST",
        "crm/v3/objects/contacts/search",
        [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{customer_email}"}]}]',
          },
        ],
        [
          {
            name: "results[0].properties.firstname",
            customName: "contact_name",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "contact_company",
            type: "string",
          },
          {
            name: "results[0].properties.hs_lead_status",
            customName: "contact_status",
            type: "string",
          },
        ],
      ),

      // 4. Success/failure routing condition
      n("4", "condition", "Payment succeeded?", 760, {
        conditionData: [
          {
            variable: "is_success",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Slack Block Kit alert
      slackNode(
        "5",
        "Slack: Payment alert (Block Kit)",
        1000,
        "*{severity} — Stripe Payment Alert*\\nEvent: {event_type}\\nCharge: {charge_id}\\nAmount: ${amount} {currency}\\nCustomer: {contact_name} ({customer_email})\\nCompany: {contact_company}\\nStatus: {status}\\nFailure: {failure_code} {failure_message}\\nSeverity: {severity}",
      ),

      // 6. Dispute SLA timer (delay)
      n("6", "delayAction", "Wait for dispute SLA response", 1240, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 24,
          delayUnit: "hours",
        },
      }),

      // 7. Escalation on no-ack
      slackNode(
        "7",
        "Slack: Escalation — dispute unacknowledged",
        1480,
        ":rotating_light: *ESCALATION: Unacknowledged Dispute*\\nCharge: {charge_id}\\nAmount: ${amount} {currency}\\nCustomer: {contact_name} ({customer_email})\\nSeverity: {severity}\\n24h SLA breached — immediate action required.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // success -> standard alert
      e("4", "5", "output_2"), // failure -> same alert (formatted differently by severity)
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 673. STRIPE SUBSCRIPTION LIFECYCLE MANAGER (10 nodes)
  // ===================================================================

  "Stripe Subscription Lifecycle Manager": {
    summary:
      "Manages the complete Stripe subscription lifecycle from creation to cancellation. A Stripe subscription webhook triggers event routing via runCode, creates/updates internal records, sends trial-ending reminders, handles past-due dunning, processes cancellations, updates a dashboard in Sheets, syncs to HubSpot CRM, sends lifecycle Slack alerts, and generates monthly cohort reports.",
    nodes: [
      trigger("Webhook: Stripe subscription event", "webhook_app", {
        source: "Stripe",
        event:
          "customer.subscription.created | updated | deleted | trial_will_end",
        howItStarts:
          "Stripe fires a webhook on any subscription lifecycle event, triggering comprehensive lifecycle management",
        setup:
          "Configure Stripe webhooks for all customer.subscription.* events. Ensure HubSpot, Slack, and Google Sheets are connected.",
        inputFields: [],
      }),

      // 2. Event type routing (runCode)
      n("2", "runCode", "Route subscription event type", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `const payload = JSON.parse(vars.webhook_payload || "{}");
const data = payload.data ? payload.data.object : payload;
const event_type = payload.type || "unknown";
const sub_id = data.id || "";
const customer_id = data.customer || "";
const customer_email = data.customer_email || "";
const plan_id = data.items?.data?.[0]?.price?.id || data.plan?.id || "";
const plan_name = data.items?.data?.[0]?.price?.nickname || data.plan?.nickname || "Subscription";
const status = data.status || "";
const mrr = ((data.items?.data?.[0]?.price?.unit_amount || 0) / 100).toFixed(2);
const interval = data.items?.data?.[0]?.price?.recurring?.interval || "month";
const cancel_at_period_end = data.cancel_at_period_end ? "yes" : "no";
const trial_end = data.trial_end ? new Date(data.trial_end * 1000).toISOString() : "";
const is_trial_ending = event_type.includes("trial_will_end") ? "yes" : "no";
const is_past_due = status === "past_due" ? "yes" : "no";
const is_canceled = status === "canceled" || event_type.includes("deleted") ? "yes" : "no";
const is_new = event_type.includes("created") ? "yes" : "no";
const lifecycle_stage = is_canceled === "yes" ? "churned" : is_past_due === "yes" ? "at_risk" : is_trial_ending === "yes" ? "trial_ending" : is_new === "yes" ? "new" : "active";
return { event_type, sub_id, customer_id, customer_email, plan_id, plan_name, status, mrr, interval, cancel_at_period_end, trial_end, is_trial_ending, is_past_due, is_canceled, is_new, lifecycle_stage };`,
          outputVariables: [
            { name: "event_type", type: "string" },
            { name: "sub_id", type: "string" },
            { name: "customer_id", type: "string" },
            { name: "customer_email", type: "string" },
            { name: "plan_id", type: "string" },
            { name: "plan_name", type: "string" },
            { name: "status", type: "string" },
            { name: "mrr", type: "string" },
            { name: "interval", type: "string" },
            { name: "cancel_at_period_end", type: "string" },
            { name: "trial_end", type: "string" },
            { name: "is_trial_ending", type: "string" },
            { name: "is_past_due", type: "string" },
            { name: "is_canceled", type: "string" },
            { name: "is_new", type: "string" },
            { name: "lifecycle_stage", type: "string" },
          ],
        },
      }),

      // 3. Internal record create/update
      sheetsNode(
        "3",
        "Create/update subscription record",
        520,
        "SubscriptionRecords",
        '[["{sub_id}","{customer_id}","{customer_email}","{plan_name}","{mrr}","{status}","{lifecycle_stage}","{event_type}","' +
          new Date().toISOString() +
          '"]]',
      ),

      // 4. Trial-ending reminder email
      emailNode(
        "4",
        "Email: Trial ending reminder",
        760,
        "{customer_email}",
        "Your {plan_name} trial is ending soon",
        "Hi there,\\n\\nYour trial for {plan_name} ends on {trial_end}. To keep your access uninterrupted, please update your billing information.\\n\\nYour plan: {plan_name} at ${mrr}/{interval}\\n\\nIf you have any questions, reply to this email and we will be happy to help.",
      ),

      // 5. Past-due dunning entry
      emailNode(
        "5",
        "Email: Payment past due notice",
        1000,
        "{customer_email}",
        "Action required: Payment past due for {plan_name}",
        "We were unable to process your payment for {plan_name} (${mrr}/{interval}).\\n\\nPlease update your payment method to avoid service interruption.\\n\\nSubscription: {sub_id}\\nStatus: {status}\\n\\nUpdate your payment method at your account settings page.",
      ),

      // 6. Cancellation processing (condition-driven)
      n("6", "condition", "Subscription canceled?", 1240, {
        conditionData: [
          {
            variable: "is_canceled",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Dashboard update (Sheets)
      sheetsNode(
        "7",
        "Update lifecycle dashboard",
        1480,
        "LifecycleDashboard",
        '[["{lifecycle_stage}","{plan_name}","{mrr}","{customer_email}","{event_type}","' +
          new Date().toISOString() +
          '"]]',
      ),

      // 8. CRM sync (HubSpot)
      hubspotNode(
        "8",
        "Sync lifecycle to HubSpot",
        1720,
        "PATCH",
        "crm/v3/objects/contacts/{hs_contact_id}",
        [
          {
            key: "properties",
            value:
              '{"subscription_status":"{status}","subscription_plan":"{plan_name}","subscription_mrr":"{mrr}","lifecycle_stage_custom":"{lifecycle_stage}","last_subscription_event":"{event_type}"}',
          },
        ],
        [{ name: "id", customName: "hs_updated_id", type: "string" }],
      ),

      // 9. Lifecycle Slack alerts
      slackNode(
        "9",
        "Slack: Subscription lifecycle alert",
        1960,
        ":bell: *Subscription Lifecycle*\\nEvent: {event_type}\\nCustomer: {customer_email}\\nPlan: {plan_name} (${mrr}/{interval})\\nStatus: {status}\\nStage: {lifecycle_stage}\\nSub ID: {sub_id}",
      ),

      // 10. Monthly cohort reporting
      sheetsNode(
        "10",
        "Log cohort data for monthly report",
        2200,
        "CohortTracking",
        '[["{customer_email}","{plan_name}","{mrr}","{lifecycle_stage}","{is_new}","{is_canceled}","' +
          new Date().toISOString().slice(0, 7) +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // canceled -> dashboard + full flow
      e("6", "7", "output_2"), // not canceled -> still update dashboard
      e("7", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ===================================================================
  // 674. STUDENT FINANCIAL AID ADVISOR BOT (8 nodes)
  // ===================================================================

  "Student Financial Aid Advisor Bot": {
    summary:
      "AI-powered financial aid advisory bot that helps students find scholarships, grants, and funding. A chat trigger starts the conversation, AI classifies intent, collects student profile data, searches scholarship/grant databases, generates tailored guidance, schedules deadline reminders, packages official links, and escalates to a human counselor when needed.",
    nodes: [
      trigger("Webhook: Student chat initiated", "webhook_app", {
        source: "Chat Widget / Student Portal",
        event: "chat.started",
        howItStarts:
          "Student initiates a financial aid inquiry via chat widget or student portal, starting the advisory flow",
        setup:
          "Configure chat widget webhook to fire on new conversation. Ensure scholarship database API access (Fastweb/Scholarships.com) and OpenAI key are configured.",
        inputFields: [],
      }),

      // 2. AI intent classification
      aiNode(
        "2",
        "AI: Classify student intent",
        280,
        "You are a financial aid advisor intake assistant. Analyze the student message to determine their intent: scholarship_search, grant_inquiry, fafsa_help, loan_comparison, appeal_guidance, or general_question. Also extract any profile info mentioned (GPA, major, income bracket, year). Respond JSON: {intent: string, gpa: string, major: string, year: string, income_bracket: string, specific_question: string, urgency: string}",
        "Student message: {webhook_payload}",
        [
          { field: "intent", as: "student_intent", type: "string" },
          { field: "gpa", as: "student_gpa", type: "string" },
          { field: "major", as: "student_major", type: "string" },
          { field: "year", as: "student_year", type: "string" },
          { field: "income_bracket", as: "income_bracket", type: "string" },
          {
            field: "specific_question",
            as: "specific_question",
            type: "string",
          },
          { field: "urgency", as: "urgency", type: "string" },
        ],
      ),

      // 3. Profile collection
      aiNode(
        "3",
        "AI: Build complete student profile",
        520,
        "You are a financial aid profile builder. Given partial student data, generate a complete profile for scholarship matching. Fill in reasonable defaults where data is missing and flag what additional info would improve matches. Respond JSON: {profile_summary: string, eligibility_factors: string, missing_fields: string, student_email: string, profile_complete: string}",
        "Intent: {student_intent}. GPA: {student_gpa}. Major: {student_major}. Year: {student_year}. Income: {income_bracket}. Question: {specific_question}.",
        [
          { field: "profile_summary", as: "profile_summary", type: "string" },
          {
            field: "eligibility_factors",
            as: "eligibility_factors",
            type: "string",
          },
          { field: "missing_fields", as: "missing_fields", type: "string" },
          { field: "student_email", as: "student_email", type: "string" },
          { field: "profile_complete", as: "profile_complete", type: "string" },
        ],
      ),

      // 4. Scholarship/grant database search
      n("4", "hitApi", "Search scholarship database", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.fastweb.com/v1/scholarships?gpa={student_gpa}&major={student_major}&year={student_year}&limit=10",
          headers: [
            { key: "Authorization", value: "Bearer {fastweb_api_key}" },
            { key: "Accept", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "scholarships",
              customName: "scholarships_json",
              type: "string",
            },
            {
              name: "total_results",
              customName: "scholarship_count",
              type: "number",
            },
          ],
        },
      }),

      // 5. AI tailored guidance
      aiNode(
        "5",
        "AI: Generate tailored guidance",
        1000,
        "You are an expert financial aid counselor. Given a student profile and matching scholarships/grants, create personalized, actionable guidance. Prioritize by deadline urgency, match strength, and award amount. Include specific next steps. Respond JSON: {guidance_text: string, top_scholarships: string, total_potential_value: string, next_steps: string, deadline_nearest: string, needs_counselor: string}",
        "Profile: {profile_summary}. Eligibility: {eligibility_factors}. Scholarships found: {scholarships_json}. Original question: {specific_question}.",
        [
          { field: "guidance_text", as: "guidance_text", type: "string" },
          { field: "top_scholarships", as: "top_scholarships", type: "string" },
          {
            field: "total_potential_value",
            as: "total_potential_value",
            type: "string",
          },
          { field: "next_steps", as: "next_steps", type: "string" },
          { field: "deadline_nearest", as: "deadline_nearest", type: "string" },
          { field: "needs_counselor", as: "needs_counselor", type: "string" },
        ],
      ),

      // 6. Deadline reminder scheduling (delay)
      n("6", "delayAction", "Schedule deadline reminder", 1240, {
        delayDetails: { delayType: "fixed", delayValue: 7, delayUnit: "days" },
      }),

      // 7. Official links packaging
      emailNode(
        "7",
        "Email: Financial aid guidance & links",
        1480,
        "{student_email}",
        "Your Personalized Financial Aid Guide — {scholarship_count} Opportunities Found",
        "Hi,\\n\\nBased on your profile, here is your personalized financial aid guidance:\\n\\n{guidance_text}\\n\\nTop Scholarships:\\n{top_scholarships}\\n\\nTotal Potential Value: {total_potential_value}\\n\\nNearest Deadline: {deadline_nearest}\\n\\nNext Steps:\\n{next_steps}\\n\\nOfficial Resources:\\n- FAFSA: https://studentaid.gov/h/apply-for-aid/fafsa\\n- Federal Grants: https://studentaid.gov/understand-aid/types/grants\\n- Loan Comparison: https://studentaid.gov/understand-aid/types/loans\\n\\nReply to this email if you need help with your applications.",
      ),

      // 8. Counselor escalation condition
      n("8", "condition", "Needs human counselor?", 1720, {
        conditionData: [
          {
            variable: "needs_counselor",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "7", "output_2"), // no counselor needed -> done (terminal)
    ],
  },

  // ===================================================================
  // 675. SUBSCRIPTION CANCELLATION SAVE ATTEMPT (9 nodes)
  // ===================================================================

  "Subscription Cancellation Save Attempt": {
    summary:
      "Attempts to save canceling subscriptions with AI-driven retention plays. A cancellation trigger pulls usage/NPS/tenure data, AI selects the best save play, routes by cancel reason via condition, sends price-save/feature-discovery/competitor-response emails, monitors the save window via delay, and logs outcomes to Sheets.",
    nodes: [
      trigger("Webhook: Cancellation initiated", "webhook_app", {
        source: "Stripe / Internal App",
        event: "subscription.cancel_scheduled",
        howItStarts:
          "Fires when a customer schedules subscription cancellation, initiating the retention save attempt",
        setup:
          "Configure webhook for cancellation intent events. Ensure Stripe, NPS system, and email service are connected. Create a SaveAttempts Google Sheet.",
        inputFields: [],
      }),

      // 2. Usage/NPS/tenure pull (runCode)
      n("2", "runCode", "Pull usage, NPS & tenure data", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `const payload = JSON.parse(vars.webhook_payload || "{}");
const data = payload.data ? payload.data.object : payload;
const customer_email = data.customer_email || data.email || "";
const customer_name = data.customer_name || data.name || "Customer";
const sub_id = data.id || data.subscription_id || "";
const plan = data.items?.data?.[0]?.price?.nickname || data.plan_name || "Pro";
const mrr = ((data.items?.data?.[0]?.price?.unit_amount || data.amount || 0) / 100).toFixed(2);
const created = data.created || data.start_date || 0;
const tenureMonths = created ? Math.floor((Date.now() / 1000 - created) / (30 * 86400)) : 0;
const cancelReason = data.cancellation_details?.comment || data.cancel_reason || "not_specified";
const npsScore = data.metadata?.nps_score || "unknown";
const usagePercent = data.metadata?.usage_percent || "unknown";
const isHighValue = parseFloat(mrr) >= 50 || tenureMonths >= 6 ? "yes" : "no";
const reasonCategory = cancelReason.toLowerCase().includes("price") ? "price"
  : cancelReason.toLowerCase().includes("competitor") ? "competitor"
  : cancelReason.toLowerCase().includes("feature") || cancelReason.toLowerCase().includes("use") ? "low_use"
  : "other";
return { customer_email, customer_name, sub_id, plan, mrr, tenure_months: String(tenureMonths), cancel_reason: cancelReason, nps_score: npsScore, usage_percent: usagePercent, is_high_value: isHighValue, reason_category: reasonCategory };`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "sub_id", type: "string" },
            { name: "plan", type: "string" },
            { name: "mrr", type: "string" },
            { name: "tenure_months", type: "string" },
            { name: "cancel_reason", type: "string" },
            { name: "nps_score", type: "string" },
            { name: "usage_percent", type: "string" },
            { name: "is_high_value", type: "string" },
            { name: "reason_category", type: "string" },
          ],
        },
      }),

      // 3. AI save play selection
      aiNode(
        "3",
        "AI: Select best save play",
        520,
        "You are a subscription retention expert. Given customer data, select the best save strategy. Consider tenure, MRR, NPS, usage, and cancel reason. Respond JSON: {save_strategy: string, discount_percent: string, offer_description: string, tone: string, confidence: string}",
        "Customer: {customer_name}. Plan: {plan} at ${mrr}/mo. Tenure: {tenure_months} months. NPS: {nps_score}. Usage: {usage_percent}%. Cancel reason: {cancel_reason}. Category: {reason_category}. High value: {is_high_value}.",
        [
          { field: "save_strategy", as: "save_strategy", type: "string" },
          { field: "discount_percent", as: "discount_percent", type: "string" },
          {
            field: "offer_description",
            as: "offer_description",
            type: "string",
          },
          { field: "tone", as: "save_tone", type: "string" },
          { field: "confidence", as: "save_confidence", type: "string" },
        ],
      ),

      // 4. Reason routing condition
      n("4", "condition", "Cancel reason is price-related?", 760, {
        conditionData: [
          {
            variable: "reason_category",
            value: "price",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Price save email (true branch)
      emailNode(
        "5",
        "Email: Price save offer",
        1000,
        "{customer_email}",
        "We'd hate to see you go, {customer_name} — Special offer inside",
        "Hi {customer_name},\\n\\nWe noticed you are considering canceling your {plan} subscription. We value your {tenure_months} months with us.\\n\\n{offer_description}\\n\\nDiscount: {discount_percent}% off for the next 3 months\\nYour new price: reduced from ${mrr}/mo\\n\\nTo accept this offer, simply click below — no changes needed on your end.\\n\\nWe are always improving based on feedback. Your reason ({cancel_reason}) helps us get better.",
      ),

      // 6. Low-use feature discovery email (false branch — further routes)
      emailNode(
        "6",
        "Email: Feature discovery for low-use",
        1000,
        "{customer_email}",
        "{customer_name}, you might be missing these {plan} features",
        "Hi {customer_name},\\n\\nWe noticed you might not be using some of the most powerful features in your {plan} plan.\\n\\n{offer_description}\\n\\nHere are features that customers like you love:\\n- Advanced analytics dashboard\\n- API integrations\\n- Priority support\\n\\nWould you like a personalized walkthrough? Reply to this email and we will set one up.\\n\\nUsage tip: Customers who use these features see 3x more value.",
        300,
      ),

      // 7. Competitor response email
      emailNode(
        "7",
        "Email: Competitor response offer",
        1000,
        "{customer_email}",
        "{customer_name}, here's why customers come back",
        "Hi {customer_name},\\n\\nWe understand you are exploring alternatives. Here is what makes us different:\\n\\n{offer_description}\\n\\nOur {plan} plan includes exclusive features competitors charge extra for.\\n\\nTo make the switch easier, we are offering {discount_percent}% off for 6 months.\\n\\nTenure: {tenure_months} months | MRR: ${mrr}",
        520,
      ),

      // 8. Save window monitoring (delay)
      n("8", "delayAction", "Wait for save window (48h)", 1240, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 48,
          delayUnit: "hours",
        },
      }),

      // 9. Outcome logging (Sheets)
      sheetsNode(
        "9",
        "Log save attempt outcome",
        1480,
        "SaveAttempts",
        '[["{customer_email}","{customer_name}","{plan}","{mrr}","{tenure_months}","{cancel_reason}","{reason_category}","{save_strategy}","{discount_percent}","{save_confidence}","' +
          new Date().toISOString() +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // price reason -> price save
      e("4", "6", "output_2"), // non-price -> feature discovery
      e("5", "8"),
      e("6", "7"), // low-use also gets competitor offer
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 676. SUBSCRIPTION PAUSE PREVENTION (8 nodes)
  // ===================================================================

  "Subscription Pause Prevention": {
    summary:
      "Prevents subscription pauses by offering AI-generated alternatives. A pause intent trigger pulls usage/billing data, AI generates alternatives, presents choices via in-app message, captures user selection, applies the chosen alternative via Stripe, checks grace period eligibility via condition, and flags retention in HubSpot.",
    nodes: [
      trigger("Webhook: Pause intent detected", "webhook_app", {
        source: "Internal App / Stripe",
        event: "subscription.pause_intent",
        howItStarts:
          "Fires when a customer clicks 'Pause subscription' or similar intent is detected, initiating prevention flow",
        setup:
          "Configure webhook for pause intent events from your app or Stripe customer portal. Ensure Stripe and HubSpot API access.",
        inputFields: [],
      }),

      // 2. Usage/billing pull (runCode)
      n("2", "runCode", "Pull usage & billing context", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `const payload = JSON.parse(vars.webhook_payload || "{}");
const data = payload.data ? payload.data.object : payload;
const customer_email = data.customer_email || data.email || "";
const customer_name = data.customer_name || data.name || "Customer";
const sub_id = data.subscription_id || data.id || "";
const customer_id = data.customer || data.customer_id || "";
const plan = data.plan_name || data.items?.data?.[0]?.price?.nickname || "Standard";
const mrr = ((data.amount || data.items?.data?.[0]?.price?.unit_amount || 0) / 100).toFixed(2);
const billingCycleEnd = data.current_period_end ? new Date(data.current_period_end * 1000).toISOString().split("T")[0] : "";
const daysRemaining = data.current_period_end ? Math.max(0, Math.floor((data.current_period_end - Date.now() / 1000) / 86400)) : 0;
const usageLevel = data.metadata?.usage_level || "medium";
const pauseReason = data.pause_reason || data.metadata?.pause_reason || "taking_a_break";
return { customer_email, customer_name, sub_id, customer_id, plan, mrr, billing_cycle_end: billingCycleEnd, days_remaining: String(daysRemaining), usage_level: usageLevel, pause_reason: pauseReason };`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "sub_id", type: "string" },
            { name: "customer_id", type: "string" },
            { name: "plan", type: "string" },
            { name: "mrr", type: "string" },
            { name: "billing_cycle_end", type: "string" },
            { name: "days_remaining", type: "string" },
            { name: "usage_level", type: "string" },
            { name: "pause_reason", type: "string" },
          ],
        },
      }),

      // 3. AI alternative generation
      aiNode(
        "3",
        "AI: Generate pause alternatives",
        520,
        "You are a subscription retention specialist. Instead of pausing, generate 3 attractive alternatives: downgrade to cheaper plan, reduced billing frequency, or feature-limited free tier. Tailor based on usage level and pause reason. Respond JSON: {alternative_1: string, alternative_2: string, alternative_3: string, recommended: string, savings_message: string, user_selection_prompt: string}",
        "Customer: {customer_name}. Plan: {plan} at ${mrr}/mo. Usage: {usage_level}. Pause reason: {pause_reason}. Days remaining in cycle: {days_remaining}.",
        [
          { field: "alternative_1", as: "alternative_1", type: "string" },
          { field: "alternative_2", as: "alternative_2", type: "string" },
          { field: "alternative_3", as: "alternative_3", type: "string" },
          { field: "recommended", as: "recommended_option", type: "string" },
          { field: "savings_message", as: "savings_message", type: "string" },
          {
            field: "user_selection_prompt",
            as: "selection_prompt",
            type: "string",
          },
        ],
      ),

      // 4. Choice modal (sendMessage)
      n("4", "sendMessage", "In-app: Present alternatives to user", 760, {
        data: {
          title:
            "{customer_name}, before you pause — consider these alternatives:\\n\\n1. {alternative_1}\\n2. {alternative_2}\\n3. {alternative_3}\\n\\nRecommended: {recommended_option}\\n\\n{savings_message}\\n\\n{selection_prompt}",
        },
      }),

      // 5. User selection capture (runCode)
      n("5", "runCode", "Capture user selection", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// In production, this would capture the user's choice from the in-app message callback
const selection = vars.user_response || "recommended";
const selectedAlternative = selection === "1" ? vars.alternative_1
  : selection === "2" ? vars.alternative_2
  : selection === "3" ? vars.alternative_3
  : vars.recommended_option;
const action = selection === "pause" ? "pause" : "alternative_accepted";
return { selected_alternative: selectedAlternative, user_action: action, selection_choice: selection };`,
          outputVariables: [
            { name: "selected_alternative", type: "string" },
            { name: "user_action", type: "string" },
            { name: "selection_choice", type: "string" },
          ],
        },
      }),

      // 6. Alternative application (Stripe)
      stripeNode(
        "6",
        "Apply selected alternative via Stripe",
        1240,
        "POST",
        "subscriptions/{sub_id}",
        [
          { key: "metadata[pause_prevented]", value: "true" },
          {
            key: "metadata[alternative_applied]",
            value: "{selected_alternative}",
          },
          { key: "metadata[original_plan]", value: "{plan}" },
          { key: "proration_behavior", value: "create_prorations" },
        ],
        [
          { name: "id", customName: "updated_sub_id", type: "string" },
          { name: "status", customName: "updated_status", type: "string" },
        ],
      ),

      // 7. Grace period condition
      n("7", "condition", "Within grace period (>7 days left)?", 1480, {
        conditionData: [
          {
            variable: "days_remaining",
            value: "7",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Retention flagging (HubSpot)
      hubspotNode(
        "8",
        "Flag retention in HubSpot",
        1720,
        "PATCH",
        "crm/v3/objects/contacts/{hs_contact_id}",
        [
          {
            key: "properties",
            value:
              '{"pause_prevented":"true","retention_alternative":"{selected_alternative}","pause_reason":"{pause_reason}","subscription_status":"active_retained"}',
          },
        ],
        [{ name: "id", customName: "hs_retention_id", type: "string" }],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // grace period ok -> flag retention
      e("7", "8", "output_2"), // no grace period -> still flag but different status
    ],
  },

  // ===================================================================
  // 677. SUBSCRIPTION PAYMENT FAILED (9 nodes)
  // ===================================================================

  "Subscription Payment Failed": {
    summary:
      "Comprehensive payment failure recovery with smart retry logic. A Stripe invoice.payment_failed webhook triggers reason classification via runCode, assesses churn risk, determines retry strategy, sends a card-update email, schedules a payment retry delay, checks for in-app banner via condition, sends a final warning email, and logs the dunning sequence to Sheets.",
    nodes: [
      trigger("Webhook: Stripe payment failed", "webhook_app", {
        source: "Stripe",
        event: "invoice.payment_failed",
        howItStarts:
          "Stripe fires a webhook when an invoice payment attempt fails, starting the dunning and recovery sequence",
        setup:
          "Configure Stripe webhook for invoice.payment_failed. Ensure email service and Slack are connected. Create DunningLog Google Sheet.",
        inputFields: [],
      }),

      // 2. Reason classification (runCode)
      n("2", "runCode", "Classify failure reason", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `const payload = JSON.parse(vars.webhook_payload || "{}");
const data = payload.data ? payload.data.object : payload;
const customer_email = data.customer_email || "";
const customer_name = data.customer_name || "Customer";
const invoice_id = data.id || "";
const sub_id = data.subscription || "";
const amount = ((data.amount_due || 0) / 100).toFixed(2);
const currency = (data.currency || "usd").toUpperCase();
const attempt_count = data.attempt_count || 1;
const next_attempt = data.next_payment_attempt ? new Date(data.next_payment_attempt * 1000).toISOString() : "none";
const charge = data.charge ? (typeof data.charge === "object" ? data.charge : {}) : {};
const failure_code = charge.failure_code || data.last_payment_error?.code || "unknown";
const failure_message = charge.failure_message || data.last_payment_error?.message || "Payment could not be processed";
const isCardDecline = failure_code.includes("card_declined") || failure_code.includes("insufficient");
const isExpired = failure_code.includes("expired");
const isRetryable = !failure_code.includes("stolen") && !failure_code.includes("fraudulent") && attempt_count < 4;
const reason_category = isExpired ? "card_expired" : isCardDecline ? "card_declined" : "processing_error";
return { customer_email, customer_name, invoice_id, sub_id, amount, currency, attempt_count: String(attempt_count), next_attempt, failure_code, failure_message, reason_category, is_retryable: isRetryable ? "yes" : "no" };`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "invoice_id", type: "string" },
            { name: "sub_id", type: "string" },
            { name: "amount", type: "string" },
            { name: "currency", type: "string" },
            { name: "attempt_count", type: "string" },
            { name: "next_attempt", type: "string" },
            { name: "failure_code", type: "string" },
            { name: "failure_message", type: "string" },
            { name: "reason_category", type: "string" },
            { name: "is_retryable", type: "string" },
          ],
        },
      }),

      // 3. Churn risk assessment (AI)
      aiNode(
        "3",
        "AI: Assess churn risk",
        520,
        "You are a subscription analytics expert. Assess the churn risk given the payment failure details. Consider attempt count, failure reason, and amount. Respond JSON: {churn_risk: string, risk_score: string, recommended_action: string, urgency: string, customer_segment: string}",
        "Customer: {customer_name} ({customer_email}). Amount: ${amount}. Failure: {failure_code} — {failure_message}. Attempt: {attempt_count}. Reason: {reason_category}. Retryable: {is_retryable}.",
        [
          { field: "churn_risk", as: "churn_risk", type: "string" },
          { field: "risk_score", as: "risk_score", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          { field: "urgency", as: "urgency", type: "string" },
          { field: "customer_segment", as: "customer_segment", type: "string" },
        ],
      ),

      // 4. Retry strategy determination (runCode)
      n("4", "runCode", "Determine retry strategy", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `const attempt = parseInt(vars.attempt_count) || 1;
const retryable = vars.is_retryable === "yes";
const riskScore = parseInt(vars.risk_score) || 50;
let retryDelay = 24;
let retryAction = "retry";
if (!retryable) { retryAction = "no_retry"; retryDelay = 0; }
else if (attempt === 1) { retryDelay = 24; retryAction = "soft_retry"; }
else if (attempt === 2) { retryDelay = 72; retryAction = "medium_retry"; }
else if (attempt >= 3) { retryDelay = 168; retryAction = "final_retry"; }
const showBanner = attempt >= 2 ? "yes" : "no";
const sendFinalWarning = attempt >= 3 ? "yes" : "no";
return { retry_delay_hours: String(retryDelay), retry_action: retryAction, show_banner: showBanner, send_final_warning: sendFinalWarning };`,
          outputVariables: [
            { name: "retry_delay_hours", type: "string" },
            { name: "retry_action", type: "string" },
            { name: "show_banner", type: "string" },
            { name: "send_final_warning", type: "string" },
          ],
        },
      }),

      // 5. Card-update email
      emailNode(
        "5",
        "Email: Update payment method",
        1000,
        "{customer_email}",
        "Action needed: Payment failed for your subscription",
        "Hi {customer_name},\\n\\nWe were unable to process your payment of ${amount} {currency}.\\n\\nReason: {failure_message}\\n\\nPlease update your payment method to avoid service interruption:\\nhttps://billing.stripe.com/p/login/YOUR_PORTAL_ID\\n\\nInvoice: {invoice_id}\\nAttempt: {attempt_count}\\nNext retry: {next_attempt}\\n\\nIf you need help, reply to this email.",
      ),

      // 6. Payment retry (delay)
      n("6", "delayAction", "Wait for retry window", 1240, {
        delayDetails: {
          delayType: "fixed",
          delayValue: 24,
          delayUnit: "hours",
        },
      }),

      // 7. In-app banner condition
      n("7", "condition", "Show in-app banner (attempt >= 2)?", 1480, {
        conditionData: [
          {
            variable: "show_banner",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Final warning email
      emailNode(
        "8",
        "Email: Final payment warning",
        1720,
        "{customer_email}",
        "URGENT: Your subscription will be canceled — {customer_name}",
        "Hi {customer_name},\\n\\nThis is your final notice. We have been unable to process your payment of ${amount} {currency} after {attempt_count} attempts.\\n\\nFailure reason: {failure_message}\\n\\nYour subscription will be canceled if payment is not received.\\n\\nUpdate payment now: https://billing.stripe.com/p/login/YOUR_PORTAL_ID\\n\\nChurn risk: {churn_risk}\\n\\nPlease act immediately to keep your account active.",
      ),

      // 9. Dunning sequence logging (Sheets)
      sheetsNode(
        "9",
        "Log dunning sequence to Sheets",
        1960,
        "DunningLog",
        '[["{customer_email}","{customer_name}","{amount}","{currency}","{failure_code}","{reason_category}","{attempt_count}","{retry_action}","{churn_risk}","{risk_score}","' +
          new Date().toISOString() +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // show banner -> final warning
      e("7", "9", "output_2"), // no banner -> log only
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 678. SUBSCRIPTION PAYMENT-FAILURE RESCUE (8 nodes)
  // ===================================================================

  "Subscription Payment-Failure Rescue": {
    summary:
      "Multi-stage dunning rescue sequence for failed subscription payments. A Stripe payment_failed webhook triggers dunning stage determination via runCode, sends stage 1 gentle email, stage 2 urgent email, stage 3 final email, an SMS at late stage via Twilio, checks for recovery via condition, and logs touch attribution to Sheets.",
    nodes: [
      trigger("Webhook: Stripe payment failed", "webhook_app", {
        source: "Stripe",
        event: "invoice.payment_failed",
        howItStarts:
          "Stripe fires a webhook on payment failure, starting a multi-stage dunning rescue sequence",
        setup:
          "Configure Stripe webhook for invoice.payment_failed. Ensure Twilio SMS credentials and email service are connected.",
        inputFields: [],
      }),

      // 2. Dunning stage determination (runCode)
      n("2", "runCode", "Determine dunning stage", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `const payload = JSON.parse(vars.webhook_payload || "{}");
const data = payload.data ? payload.data.object : payload;
const customer_email = data.customer_email || "";
const customer_name = data.customer_name || "Customer";
const customer_phone = data.customer_phone || data.metadata?.phone || "";
const invoice_id = data.id || "";
const amount = ((data.amount_due || 0) / 100).toFixed(2);
const currency = (data.currency || "usd").toUpperCase();
const attempt_count = data.attempt_count || 1;
const failure_code = data.last_payment_error?.code || "card_declined";
let dunning_stage = "stage_1";
if (attempt_count >= 3) dunning_stage = "stage_3";
else if (attempt_count >= 2) dunning_stage = "stage_2";
const needs_sms = attempt_count >= 2 && customer_phone ? "yes" : "no";
const update_url = "https://billing.stripe.com/p/login/YOUR_PORTAL_ID";
return { customer_email, customer_name, customer_phone, invoice_id, amount, currency, attempt_count: String(attempt_count), failure_code, dunning_stage, needs_sms, update_url };`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "customer_phone", type: "string" },
            { name: "invoice_id", type: "string" },
            { name: "amount", type: "string" },
            { name: "currency", type: "string" },
            { name: "attempt_count", type: "string" },
            { name: "failure_code", type: "string" },
            { name: "dunning_stage", type: "string" },
            { name: "needs_sms", type: "string" },
            { name: "update_url", type: "string" },
          ],
        },
      }),

      // 3. Stage 1 gentle email
      emailNode(
        "3",
        "Email: Stage 1 — Gentle payment reminder",
        520,
        "{customer_email}",
        "Quick heads up: We couldn't process your payment",
        "Hi {customer_name},\\n\\nWe had trouble processing your payment of ${amount} {currency}.\\n\\nThis can happen for many reasons — no worries! Just update your payment method and you are all set:\\n{update_url}\\n\\nInvoice: {invoice_id}\\nAttempt: {attempt_count}\\n\\nQuestions? Just reply to this email.",
      ),

      // 4. Stage 2 urgent email
      emailNode(
        "4",
        "Email: Stage 2 — Urgent payment notice",
        760,
        "{customer_email}",
        "Payment still failing — Action needed, {customer_name}",
        "Hi {customer_name},\\n\\nWe have tried processing your payment of ${amount} {currency} multiple times without success.\\n\\nReason: {failure_code}\\n\\nTo avoid losing access, please update your payment method now:\\n{update_url}\\n\\nThis is attempt {attempt_count}. Your service may be affected if not resolved.",
      ),

      // 5. Stage 3 final email
      emailNode(
        "5",
        "Email: Stage 3 — Final notice",
        1000,
        "{customer_email}",
        "FINAL NOTICE: Subscription will be canceled — {customer_name}",
        "Hi {customer_name},\\n\\nDespite multiple attempts, we have been unable to collect your payment of ${amount} {currency}.\\n\\nYour subscription will be canceled within 48 hours unless payment is received.\\n\\nUpdate payment immediately: {update_url}\\n\\nInvoice: {invoice_id}\\nFinal attempt: {attempt_count}",
      ),

      // 6. SMS at late stage (Twilio)
      smsNode(
        "6",
        "SMS: Late-stage payment alert",
        1240,
        "{customer_phone}",
        "URGENT: Your ${amount} payment failed (attempt {attempt_count}). Update now to keep your account: {update_url}",
      ),

      // 7. Recovery check condition
      n("7", "condition", "SMS sent (late stage)?", 1480, {
        conditionData: [
          {
            variable: "needs_sms",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Touch attribution logging (Sheets)
      sheetsNode(
        "8",
        "Log touch attribution to Sheets",
        1720,
        "DunningTouches",
        '[["{customer_email}","{customer_name}","{amount}","{dunning_stage}","{attempt_count}","{failure_code}","{needs_sms}","' +
          new Date().toISOString() +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "7"),
      e("7", "6", "output_1"), // late stage -> send SMS
      e("7", "8", "output_2"), // early stage -> skip SMS
      e("6", "8"),
    ],
  },

  // ===================================================================
  // 679. SUBSCRIPTION RENEWAL ALERT (8 nodes)
  // ===================================================================

  "Subscription Renewal Alert": {
    summary:
      "Proactive subscription renewal management with cost optimization. A renewal scan schedule pulls vendor/usage/cost data, calculates utilization efficiency via runCode, generates AI recommendations, routes by urgency via condition, sends a procurement email alert, posts high-priority items to Slack, and creates calendar events for deadlines.",
    nodes: [
      trigger("Schedule: Daily renewal scan", "schedule", {
        schedule: "0 8 * * *",
        howItStarts:
          "Runs daily at 8 AM to scan for upcoming subscription renewals across all vendor subscriptions",
        setup:
          "Set daily schedule at 8 AM. Connect vendor subscription tracking system and Google Calendar API. Create a RenewalAlerts Google Sheet.",
        inputFields: [],
      }),

      // 2. Vendor/usage/cost pull
      stripeNode(
        "2",
        "Pull active subscriptions from Stripe",
        280,
        "GET",
        "subscriptions?status=active&limit=100",
        [],
        [
          { name: "data", customName: "subscriptions_json", type: "string" },
          { name: "has_more", customName: "has_more_subs", type: "boolean" },
        ],
      ),

      // 3. Utilization efficiency calc (runCode)
      n("3", "runCode", "Calculate utilization & renewal urgency", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `const subs = JSON.parse(vars.subscriptions_json || "[]");
const now = Date.now() / 1000;
const upcoming = [];
let totalMonthlySpend = 0;
for (const sub of subs) {
  const endTs = sub.current_period_end || 0;
  const daysUntilRenewal = Math.floor((endTs - now) / 86400);
  const name = sub.items?.data?.[0]?.price?.nickname || sub.plan?.nickname || "Subscription";
  const amt = ((sub.items?.data?.[0]?.price?.unit_amount || 0) / 100);
  totalMonthlySpend += amt;
  if (daysUntilRenewal <= 30) {
    const urgency = daysUntilRenewal <= 7 ? "critical" : daysUntilRenewal <= 14 ? "high" : "medium";
    upcoming.push({ name, amount: amt.toFixed(2), days_left: daysUntilRenewal, urgency, sub_id: sub.id, customer: sub.customer });
  }
}
const sortedUpcoming = upcoming.sort((a, b) => a.days_left - b.days_left);
const renewalSummary = sortedUpcoming.map(s => s.name + " ($" + s.amount + "/mo) — " + s.days_left + " days — " + s.urgency).join("\\n");
const hasCritical = sortedUpcoming.some(s => s.urgency === "critical") ? "yes" : "no";
return { renewal_summary: renewalSummary, upcoming_count: String(sortedUpcoming.length), total_monthly_spend: totalMonthlySpend.toFixed(2), has_critical: hasCritical, upcoming_json: JSON.stringify(sortedUpcoming) };`,
          outputVariables: [
            { name: "renewal_summary", type: "string" },
            { name: "upcoming_count", type: "string" },
            { name: "total_monthly_spend", type: "string" },
            { name: "has_critical", type: "string" },
            { name: "upcoming_json", type: "string" },
          ],
        },
      }),

      // 4. AI recommendation
      aiNode(
        "4",
        "AI: Renewal recommendations",
        760,
        "You are a procurement optimization specialist. Analyze upcoming subscription renewals and provide recommendations: renegotiate, consolidate, cancel, or auto-renew. Consider cost, usage patterns, and market alternatives. Respond JSON: {recommendations: string, potential_savings: string, action_items: string, priority_renewal: string, procurement_email: string}",
        "Upcoming renewals ({upcoming_count}): {renewal_summary}. Total monthly spend: ${total_monthly_spend}. Critical items: {has_critical}.",
        [
          {
            field: "recommendations",
            as: "renewal_recommendations",
            type: "string",
          },
          {
            field: "potential_savings",
            as: "potential_savings",
            type: "string",
          },
          { field: "action_items", as: "action_items", type: "string" },
          { field: "priority_renewal", as: "priority_renewal", type: "string" },
          {
            field: "procurement_email",
            as: "procurement_email",
            type: "string",
          },
        ],
      ),

      // 5. Urgency condition
      n("5", "condition", "Critical renewals within 7 days?", 1000, {
        conditionData: [
          {
            variable: "has_critical",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Procurement email alert
      emailNode(
        "6",
        "Email: Renewal alert to procurement",
        1240,
        "{procurement_email}",
        "Subscription Renewal Alert: {upcoming_count} renewals due — ${potential_savings} savings possible",
        "Procurement Team,\\n\\nUpcoming Subscription Renewals:\\n\\n{renewal_summary}\\n\\nTotal Monthly Spend: ${total_monthly_spend}\\n\\nRecommendations:\\n{renewal_recommendations}\\n\\nPotential Savings: ${potential_savings}\\n\\nAction Items:\\n{action_items}\\n\\nPriority: {priority_renewal}",
      ),

      // 7. High-priority Slack post
      slackNode(
        "7",
        "Slack: Critical renewal alert",
        1480,
        ":alarm_clock: *Critical Subscription Renewals*\\n{upcoming_count} subscriptions due for renewal\\nCritical: {has_critical}\\n\\nPriority: {priority_renewal}\\n\\nPotential Savings: ${potential_savings}\\n\\n{renewal_recommendations}",
      ),

      // 8. Calendar event creation
      n("8", "hitApi", "Create Google Calendar renewal events", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/calendar/v3/calendars/primary/events",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "summary",
              value: "Subscription Renewal: {priority_renewal}",
            },
            {
              key: "description",
              value:
                "Renewals due:\\n{renewal_summary}\\n\\nAction Items:\\n{action_items}",
            },
            {
              key: "start",
              value:
                '{"dateTime":"' +
                new Date(Date.now() + 7 * 86400000).toISOString() +
                '"}',
            },
            {
              key: "end",
              value:
                '{"dateTime":"' +
                new Date(Date.now() + 7 * 86400000 + 3600000).toISOString() +
                '"}',
            },
          ],
          variables: [
            { name: "id", customName: "calendar_event_id", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "7", "output_1"), // critical -> Slack
      e("5", "6", "output_2"), // not critical -> email only
      e("7", "6"),
      e("6", "8"),
    ],
  },

  // ===================================================================
  // 680. SUBSCRIPTION SKIP OR MODIFY OFFER (8 nodes)
  // ===================================================================

  "Subscription Skip or Modify Offer": {
    summary:
      "Intercepts skip/cancel intent and offers modification alternatives. A skip/cancel intent trigger pulls the upcoming order from Stripe, AI generates alternatives, presents choices via in-app message, routes by selection via condition, modifies the Stripe subscription accordingly, sends a confirmation email, and logs save outcomes to Sheets.",
    nodes: [
      trigger("Webhook: Skip or cancel intent", "webhook_app", {
        source: "Internal App / Customer Portal",
        event: "subscription.skip_intent | subscription.modify_intent",
        howItStarts:
          "Fires when a customer attempts to skip a billing cycle or modify their subscription, triggering an offer flow",
        setup:
          "Configure webhook for skip/modify intent events. Ensure Stripe API access for subscription modifications.",
        inputFields: [],
      }),

      // 2. Upcoming order pull (Stripe)
      n("2", "runCode", "Parse intent & pull subscription details", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `const payload = JSON.parse(vars.webhook_payload || "{}");
const data = payload.data ? payload.data.object : payload;
const customer_email = data.customer_email || data.email || "";
const customer_name = data.customer_name || data.name || "Customer";
const sub_id = data.subscription_id || data.id || "";
const customer_id = data.customer || data.customer_id || "";
const plan = data.plan_name || data.items?.data?.[0]?.price?.nickname || "Monthly Plan";
const amount = ((data.amount || data.items?.data?.[0]?.price?.unit_amount || 0) / 100).toFixed(2);
const next_billing = data.current_period_end ? new Date(data.current_period_end * 1000).toISOString().split("T")[0] : "";
const intent_type = data.intent_type || (payload.type || "").includes("skip") ? "skip" : "modify";
const skip_reason = data.skip_reason || data.metadata?.reason || "not_needed_this_cycle";
return { customer_email, customer_name, sub_id, customer_id, plan, amount, next_billing, intent_type, skip_reason };`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "sub_id", type: "string" },
            { name: "customer_id", type: "string" },
            { name: "plan", type: "string" },
            { name: "amount", type: "string" },
            { name: "next_billing", type: "string" },
            { name: "intent_type", type: "string" },
            { name: "skip_reason", type: "string" },
          ],
        },
      }),

      // 3. AI alternative generation
      aiNode(
        "3",
        "AI: Generate skip/modify alternatives",
        520,
        "You are a subscription flexibility expert. Given a customer's intent to skip or modify, generate 3 appealing alternatives: partial delivery, product swap, schedule shift, or discount. Respond JSON: {option_1: string, option_2: string, option_3: string, recommended: string, savings_note: string}",
        "Customer: {customer_name}. Plan: {plan} at ${amount}. Intent: {intent_type}. Reason: {skip_reason}. Next billing: {next_billing}.",
        [
          { field: "option_1", as: "option_1", type: "string" },
          { field: "option_2", as: "option_2", type: "string" },
          { field: "option_3", as: "option_3", type: "string" },
          { field: "recommended", as: "recommended_option", type: "string" },
          { field: "savings_note", as: "savings_note", type: "string" },
        ],
      ),

      // 4. In-app choices (sendMessage)
      n("4", "sendMessage", "In-app: Present modification options", 760, {
        data: {
          title:
            "Hey {customer_name}, we have some options for you:\\n\\n1. {option_1}\\n2. {option_2}\\n3. {option_3}\\n\\nRecommended: {recommended_option}\\n{savings_note}\\n\\nOr proceed with your original request to {intent_type}.",
        },
      }),

      // 5. Selection routing condition
      n("5", "condition", "Customer accepted an alternative?", 1000, {
        conditionData: [
          {
            variable: "user_action",
            value: "alternative_accepted",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Stripe subscription modification
      stripeNode(
        "6",
        "Modify subscription via Stripe",
        1240,
        "POST",
        "subscriptions/{sub_id}",
        [
          { key: "metadata[modification_type]", value: "{intent_type}" },
          { key: "metadata[selected_option]", value: "{recommended_option}" },
          { key: "metadata[original_reason]", value: "{skip_reason}" },
          { key: "proration_behavior", value: "create_prorations" },
        ],
        [
          { name: "id", customName: "modified_sub_id", type: "string" },
          { name: "status", customName: "sub_status", type: "string" },
        ],
      ),

      // 7. Confirmation email
      emailNode(
        "7",
        "Email: Modification confirmation",
        1480,
        "{customer_email}",
        "Your {plan} subscription has been updated, {customer_name}",
        "Hi {customer_name},\\n\\nGreat news! Your subscription has been updated as requested.\\n\\nPlan: {plan}\\nChange: {recommended_option}\\nNext billing: {next_billing}\\n\\n{savings_note}\\n\\nIf you need any further changes, just let us know.",
      ),

      // 8. Save outcome logging (Sheets)
      sheetsNode(
        "8",
        "Log save outcome to Sheets",
        1720,
        "SkipModifyOutcomes",
        '[["{customer_email}","{customer_name}","{plan}","{amount}","{intent_type}","{skip_reason}","{recommended_option}","{sub_status}","' +
          new Date().toISOString() +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // accepted alternative -> modify
      e("5", "8", "output_2"), // declined -> log skip
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 681. SUBSCRIPTION SPEND TRACKER (8 nodes)
  // ===================================================================

  "Subscription Spend Tracker": {
    summary:
      "Monthly subscription spend analysis with AI consolidation suggestions. A monthly schedule pulls active subscriptions and actual Stripe charges, computes spend breakdowns via runCode, checks threshold breaches via condition, generates AI consolidation suggestions, emails department owners, and updates an executive dashboard in Sheets.",
    nodes: [
      trigger("Schedule: Monthly spend analysis", "schedule", {
        schedule: "0 9 1 * *",
        howItStarts:
          "Runs on the 1st of every month at 9 AM to analyze subscription spend across all departments",
        setup:
          "Set monthly schedule (1st of month, 9 AM). Connect Stripe API for charge data. Create SpendTracker and ExecutiveDashboard Google Sheets.",
        inputFields: [],
      }),

      // 2. Active subscription pull
      stripeNode(
        "2",
        "Pull active subscriptions",
        280,
        "GET",
        "subscriptions?status=active&limit=100",
        [],
        [{ name: "data", customName: "active_subs_json", type: "string" }],
      ),

      // 3. Actual charges pull (Stripe)
      stripeNode(
        "3",
        "Pull last month charges",
        520,
        "GET",
        "charges?limit=100&created[gte]={month_start_unix}",
        [],
        [{ name: "data", customName: "charges_json", type: "string" }],
      ),

      // 4. Spend computation (runCode)
      n("4", "runCode", "Compute spend breakdown & thresholds", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `const subs = JSON.parse(vars.active_subs_json || "[]");
const charges = JSON.parse(vars.charges_json || "[]");
let totalExpected = 0;
let totalActual = 0;
const byDepartment = {};
const byVendor = {};
for (const sub of subs) {
  const amt = ((sub.items?.data?.[0]?.price?.unit_amount || 0) / 100);
  const dept = sub.metadata?.department || "General";
  const vendor = sub.items?.data?.[0]?.price?.nickname || "Unknown";
  totalExpected += amt;
  byDepartment[dept] = (byDepartment[dept] || 0) + amt;
  byVendor[vendor] = (byVendor[vendor] || 0) + amt;
}
for (const ch of charges) {
  totalActual += ((ch.amount || 0) / 100);
}
const variance = totalActual - totalExpected;
const variancePercent = totalExpected > 0 ? ((variance / totalExpected) * 100).toFixed(1) : "0";
const threshold_breached = Math.abs(parseFloat(variancePercent)) > 10 ? "yes" : "no";
const deptBreakdown = Object.entries(byDepartment).map(([k,v]) => k + ": $" + Number(v).toFixed(2)).join(", ");
const vendorBreakdown = Object.entries(byVendor).map(([k,v]) => k + ": $" + Number(v).toFixed(2)).join(", ");
const deptOwnerEmail = Object.keys(byDepartment)[0] ? "{finance_team_email}" : "{finance_team_email}";
return { total_expected: totalExpected.toFixed(2), total_actual: totalActual.toFixed(2), variance: variance.toFixed(2), variance_percent: variancePercent, threshold_breached, dept_breakdown: deptBreakdown, vendor_breakdown: vendorBreakdown, sub_count: String(subs.length), dept_owner_email: deptOwnerEmail };`,
          outputVariables: [
            { name: "total_expected", type: "string" },
            { name: "total_actual", type: "string" },
            { name: "variance", type: "string" },
            { name: "variance_percent", type: "string" },
            { name: "threshold_breached", type: "string" },
            { name: "dept_breakdown", type: "string" },
            { name: "vendor_breakdown", type: "string" },
            { name: "sub_count", type: "string" },
            { name: "dept_owner_email", type: "string" },
          ],
        },
      }),

      // 5. Threshold condition
      n("5", "condition", "Spend variance > 10%?", 1000, {
        conditionData: [
          {
            variable: "threshold_breached",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. AI consolidation suggestions
      aiNode(
        "6",
        "AI: Generate consolidation suggestions",
        1240,
        "You are a SaaS spend optimization consultant. Analyze subscription spend data and suggest consolidations, renegotiations, or cancellations. Respond JSON: {suggestions: string, quick_wins: string, estimated_savings: string, risk_items: string}",
        "Total expected: ${total_expected}. Actual: ${total_actual}. Variance: ${variance} ({variance_percent}%). By department: {dept_breakdown}. By vendor: {vendor_breakdown}. Active subs: {sub_count}.",
        [
          {
            field: "suggestions",
            as: "consolidation_suggestions",
            type: "string",
          },
          { field: "quick_wins", as: "quick_wins", type: "string" },
          {
            field: "estimated_savings",
            as: "estimated_savings",
            type: "string",
          },
          { field: "risk_items", as: "risk_items", type: "string" },
        ],
      ),

      // 7. Department owner email
      emailNode(
        "7",
        "Email: Monthly spend report",
        1480,
        "{dept_owner_email}",
        "Monthly Subscription Spend: ${total_actual} ({variance_percent}% variance)",
        "Monthly Subscription Spend Report\\n\\nTotal Expected: ${total_expected}\\nTotal Actual: ${total_actual}\\nVariance: ${variance} ({variance_percent}%)\\n\\nBy Department:\\n{dept_breakdown}\\n\\nBy Vendor:\\n{vendor_breakdown}\\n\\nConsolidation Suggestions:\\n{consolidation_suggestions}\\n\\nQuick Wins:\\n{quick_wins}\\n\\nEstimated Savings: {estimated_savings}\\n\\nRisk Items:\\n{risk_items}",
      ),

      // 8. Executive dashboard (Sheets)
      sheetsNode(
        "8",
        "Update executive dashboard",
        1720,
        "ExecutiveDashboard",
        '[["' +
          new Date().toISOString().slice(0, 7) +
          '","{total_expected}","{total_actual}","{variance}","{variance_percent}","{sub_count}","{estimated_savings}","{threshold_breached}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // threshold breached -> AI suggestions
      e("5", "7", "output_2"), // within threshold -> email directly
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 682. SUBSCRIPTION-CANCELED WINBACK FINANCE (8 nodes)
  // ===================================================================

  "Subscription-Canceled Winback Finance": {
    summary:
      "Multi-touch winback sequence for canceled subscriptions with ROI tracking. A cancellation trigger captures tenure/value snapshot, checks exclusion criteria via condition, sends day-7/day-14/day-21 winback emails with escalating offers, tracks reactivation, and logs ROI to Sheets.",
    nodes: [
      trigger("Webhook: Subscription canceled", "webhook_app", {
        source: "Stripe",
        event: "customer.subscription.deleted",
        howItStarts:
          "Stripe fires a webhook when a subscription is fully canceled, starting the winback sequence",
        setup:
          "Configure Stripe webhook for customer.subscription.deleted. Create a WinbackROI Google Sheet. Ensure email service is connected.",
        inputFields: [],
      }),

      // 2. Tenure/value snapshot (runCode)
      n("2", "runCode", "Capture tenure & value snapshot", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `const payload = JSON.parse(vars.webhook_payload || "{}");
const data = payload.data ? payload.data.object : payload;
const customer_email = data.customer_email || data.metadata?.email || "";
const customer_name = data.customer_name || data.metadata?.name || "Customer";
const customer_id = data.customer || "";
const plan = data.items?.data?.[0]?.price?.nickname || data.plan?.nickname || "Subscription";
const mrr = ((data.items?.data?.[0]?.price?.unit_amount || data.plan?.amount || 0) / 100).toFixed(2);
const created = data.created || 0;
const canceled_at = data.canceled_at || Math.floor(Date.now() / 1000);
const tenureMonths = Math.floor((canceled_at - created) / (30 * 86400));
const ltv = (parseFloat(mrr) * tenureMonths).toFixed(2);
const cancelReason = data.cancellation_details?.comment || data.metadata?.cancel_reason || "not_specified";
const isExcluded = cancelReason.includes("fraud") || cancelReason.includes("abuse") || parseFloat(mrr) < 5 ? "yes" : "no";
return { customer_email, customer_name, customer_id, plan, mrr, tenure_months: String(tenureMonths), ltv, cancel_reason: cancelReason, is_excluded: isExcluded };`,
          outputVariables: [
            { name: "customer_email", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "customer_id", type: "string" },
            { name: "plan", type: "string" },
            { name: "mrr", type: "string" },
            { name: "tenure_months", type: "string" },
            { name: "ltv", type: "string" },
            { name: "cancel_reason", type: "string" },
            { name: "is_excluded", type: "string" },
          ],
        },
      }),

      // 3. Exclusion check condition
      n("3", "condition", "Eligible for winback (not excluded)?", 520, {
        conditionData: [
          {
            variable: "is_excluded",
            value: "no",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Day-7 win-back email
      emailNode(
        "4",
        "Email: Day 7 — We miss you",
        760,
        "{customer_email}",
        "We miss you, {customer_name} — Come back to {plan}",
        "Hi {customer_name},\\n\\nIt has been a week since you left, and we miss having you.\\n\\nDuring your {tenure_months} months with {plan}, you were a valued member.\\n\\nWe would love to have you back. As a returning customer, enjoy 20% off your first 3 months.\\n\\nReactivate your {plan} plan: https://app.example.com/reactivate?cid={customer_id}\\n\\nWe have been making improvements based on feedback like yours.",
      ),

      // 5. Day-14 stronger offer email (delay + email)
      n("5", "delayAction", "Wait 7 more days (Day 14)", 1000, {
        delayDetails: { delayType: "fixed", delayValue: 7, delayUnit: "days" },
      }),

      // 6. Day-14 email
      emailNode(
        "6",
        "Email: Day 14 — Stronger offer",
        1240,
        "{customer_email}",
        "{customer_name}, your {plan} plan misses you — 30% off",
        "Hi {customer_name},\\n\\nTwo weeks ago you left {plan}, and we have been working hard on improvements.\\n\\nSpecial offer just for you: 30% off for 6 months.\\n\\nYour history: {tenure_months} months, ${ltv} total value.\\n\\nReactivate now: https://app.example.com/reactivate?cid={customer_id}&offer=30pct\\n\\nThis offer expires in 7 days.",
      ),

      // 7. Day-21 final offer email (delay + email)
      n("7", "delayAction", "Wait 7 more days (Day 21)", 1480, {
        delayDetails: { delayType: "fixed", delayValue: 7, delayUnit: "days" },
      }),

      // 8. ROI logging (Sheets) + final offer combined
      sheetsNode(
        "8",
        "Log winback sequence to Sheets",
        1720,
        "WinbackROI",
        '[["{customer_email}","{customer_name}","{plan}","{mrr}","{tenure_months}","{ltv}","{cancel_reason}","winback_sequence_complete","' +
          new Date().toISOString() +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // eligible -> start winback
      e("3", "8", "output_2"), // excluded -> log only
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 683. SUPPLIER PRICE COMPARISON (8 nodes)
  // ===================================================================

  "Supplier Price Comparison": {
    summary:
      "Automated supplier price comparison with reliability-weighted ranking. A procurement request trigger loads approved suppliers, queries multiple suppliers for pricing, computes landed costs via runCode, performs reliability-weighted ranking, generates a comparison matrix in Sheets, sends a recommendation email, and logs the chosen supplier.",
    nodes: [
      trigger("Webhook: Procurement request", "webhook_app", {
        source: "Procurement System / ERP",
        event: "procurement.request.created",
        howItStarts:
          "A procurement request is submitted, triggering automated supplier price comparison across approved vendors",
        setup:
          "Configure webhook for procurement request events from your ERP or procurement system. Set up supplier API integrations.",
        inputFields: [],
      }),

      // 2. Approved supplier load (AI extraction)
      aiNode(
        "2",
        "AI: Parse request & load suppliers",
        280,
        "You are a procurement data specialist. Parse the procurement request to extract item details and match against known approved suppliers. Respond JSON: {item_name: string, quantity: string, specifications: string, requester_email: string, requester_name: string, budget_limit: string, urgency: string, supplier_list: string, supplier_count: string}",
        "Procurement request: {webhook_payload}",
        [
          { field: "item_name", as: "item_name", type: "string" },
          { field: "quantity", as: "quantity", type: "string" },
          { field: "specifications", as: "specifications", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "budget_limit", as: "budget_limit", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
          { field: "supplier_list", as: "supplier_list", type: "string" },
          { field: "supplier_count", as: "supplier_count", type: "string" },
        ],
      ),

      // 3. Multi-supplier price queries
      n("3", "hitApi", "Query supplier pricing API", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.suppliergateway.com/v2/quotes/request",
          headers: [
            { key: "Authorization", value: "Bearer {supplier_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "item", value: "{item_name}" },
            { key: "quantity", value: "{quantity}" },
            { key: "specifications", value: "{specifications}" },
            { key: "suppliers", value: "{supplier_list}" },
          ],
          variables: [
            { name: "quotes", customName: "quotes_json", type: "string" },
            {
              name: "response_count",
              customName: "quote_count",
              type: "number",
            },
          ],
        },
      }),

      // 4. Landed cost computation (runCode)
      n("4", "runCode", "Compute landed costs & reliability scores", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `const quotes = JSON.parse(vars.quotes_json || "[]");
const qty = parseInt(vars.quantity) || 1;
const results = [];
for (const q of quotes) {
  const unitPrice = parseFloat(q.unit_price) || 0;
  const shipping = parseFloat(q.shipping_cost) || 0;
  const tax = parseFloat(q.tax) || 0;
  const landedCost = (unitPrice * qty) + shipping + tax;
  const reliabilityScore = parseFloat(q.reliability_score) || 0.5;
  const leadDays = parseInt(q.lead_time_days) || 14;
  const weightedScore = (1 / landedCost) * reliabilityScore * 1000;
  results.push({ supplier: q.supplier_name, unit_price: unitPrice.toFixed(2), landed_cost: landedCost.toFixed(2), reliability: (reliabilityScore * 100).toFixed(0) + "%", lead_days: leadDays, weighted_score: weightedScore.toFixed(2) });
}
results.sort((a, b) => parseFloat(b.weighted_score) - parseFloat(a.weighted_score));
const best = results[0] || { supplier: "N/A", landed_cost: "0", weighted_score: "0" };
const comparisonMatrix = results.map((r, i) => (i + 1) + ". " + r.supplier + " — $" + r.landed_cost + " (reliability: " + r.reliability + ", lead: " + r.lead_days + "d, score: " + r.weighted_score + ")").join("\\n");
return { comparison_matrix: comparisonMatrix, best_supplier: best.supplier, best_landed_cost: best.landed_cost, best_score: best.weighted_score, results_json: JSON.stringify(results), total_quotes: String(results.length) };`,
          outputVariables: [
            { name: "comparison_matrix", type: "string" },
            { name: "best_supplier", type: "string" },
            { name: "best_landed_cost", type: "string" },
            { name: "best_score", type: "string" },
            { name: "results_json", type: "string" },
            { name: "total_quotes", type: "string" },
          ],
        },
      }),

      // 5. AI reliability-weighted ranking
      aiNode(
        "5",
        "AI: Final recommendation analysis",
        1000,
        "You are a procurement analyst. Given ranked supplier quotes, provide a final recommendation considering price, reliability, lead time, and risk factors. Respond JSON: {recommendation: string, rationale: string, risk_notes: string, alternative_pick: string}",
        "Item: {item_name} x{quantity}. Budget: ${budget_limit}. Urgency: {urgency}. Rankings:\\n{comparison_matrix}\\nBest: {best_supplier} at ${best_landed_cost}.",
        [
          {
            field: "recommendation",
            as: "final_recommendation",
            type: "string",
          },
          {
            field: "rationale",
            as: "recommendation_rationale",
            type: "string",
          },
          { field: "risk_notes", as: "risk_notes", type: "string" },
          { field: "alternative_pick", as: "alternative_pick", type: "string" },
        ],
      ),

      // 6. Comparison matrix (Sheets)
      sheetsNode(
        "6",
        "Save comparison matrix to Sheets",
        1240,
        "SupplierComparisons",
        '[["{item_name}","{quantity}","{budget_limit}","{best_supplier}","{best_landed_cost}","{total_quotes}","{final_recommendation}","' +
          new Date().toISOString() +
          '"]]',
      ),

      // 7. Recommendation email
      emailNode(
        "7",
        "Email: Supplier recommendation",
        1480,
        "{requester_email}",
        "Supplier Comparison: {item_name} — Recommended: {best_supplier}",
        "Hi {requester_name},\\n\\nYour procurement request for {item_name} (qty: {quantity}) has been analyzed.\\n\\nSupplier Comparison:\\n{comparison_matrix}\\n\\nRecommendation: {final_recommendation}\\nRationale: {recommendation_rationale}\\n\\nBest Price: {best_supplier} at ${best_landed_cost}\\nAlternative: {alternative_pick}\\n\\nRisk Notes: {risk_notes}\\n\\nBudget: ${budget_limit}\\n\\nPlease approve or request changes.",
      ),

      // 8. Chosen supplier logging
      sheetsNode(
        "8",
        "Log chosen supplier",
        1720,
        "SupplierSelections",
        '[["{item_name}","{quantity}","{best_supplier}","{best_landed_cost}","{final_recommendation}","{requester_name}","pending_approval","' +
          new Date().toISOString() +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 684. TAX FILING DEADLINE TRACKER (7 nodes)
  // ===================================================================

  "Tax Filing Deadline Tracker": {
    summary:
      "Tracks tax filing deadlines with multi-tier urgency reminders and escalation. A daily schedule scans deadlines, classifies urgency tiers via runCode, routes by proximity via condition, sends reminder emails, posts Slack alerts, escalates missing acknowledgements, and logs filing status to Sheets.",
    nodes: [
      trigger("Schedule: Daily deadline scan", "schedule", {
        schedule: "0 7 * * *",
        howItStarts:
          "Runs daily at 7 AM to scan all upcoming tax filing deadlines and send appropriate reminders",
        setup:
          "Set daily schedule at 7 AM. Create a TaxDeadlines Google Sheet with columns: Filing, Deadline, Status, Assignee, Priority. Populate with all relevant deadlines.",
        inputFields: [],
      }),

      // 2. Urgency tier classification (runCode)
      n("2", "runCode", "Classify deadline urgency tiers", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// In production, this would read from the TaxDeadlines sheet
// For template, we simulate deadline processing
const now = new Date();
const filings = [
  { name: "Q1 Estimated Tax", deadline: "2025-04-15", assignee: "tax_team@company.com", status: "pending" },
  { name: "Sales Tax Return", deadline: "2025-04-20", assignee: "tax_team@company.com", status: "pending" },
];
const results = [];
for (const f of filings) {
  const deadlineDate = new Date(f.deadline);
  const daysLeft = Math.floor((deadlineDate.getTime() - now.getTime()) / 86400000);
  let urgency = "none";
  if (daysLeft <= 1) urgency = "critical";
  else if (daysLeft <= 7) urgency = "high";
  else if (daysLeft <= 14) urgency = "medium";
  else if (daysLeft <= 30) urgency = "low";
  if (urgency !== "none") {
    results.push({ ...f, days_left: daysLeft, urgency });
  }
}
const hasCritical = results.some(r => r.urgency === "critical") ? "yes" : "no";
const hasHigh = results.some(r => r.urgency === "critical" || r.urgency === "high") ? "yes" : "no";
const summary = results.map(r => r.name + " — " + r.deadline + " (" + r.days_left + " days, " + r.urgency + ")").join("\\n");
const assigneeEmail = results[0]?.assignee || "tax_team@company.com";
return { deadline_summary: summary, deadline_count: String(results.length), has_critical: hasCritical, has_high_urgency: hasHigh, assignee_email: assigneeEmail, results_json: JSON.stringify(results) };`,
          outputVariables: [
            { name: "deadline_summary", type: "string" },
            { name: "deadline_count", type: "string" },
            { name: "has_critical", type: "string" },
            { name: "has_high_urgency", type: "string" },
            { name: "assignee_email", type: "string" },
            { name: "results_json", type: "string" },
          ],
        },
      }),

      // 3. 30/14/7/1-day condition
      n("3", "condition", "High urgency deadlines (<=7 days)?", 520, {
        conditionData: [
          {
            variable: "has_high_urgency",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Reminder email
      emailNode(
        "4",
        "Email: Tax deadline reminder",
        760,
        "{assignee_email}",
        "Tax Filing Deadlines: {deadline_count} upcoming — Action required",
        "Tax Filing Deadline Reminder\\n\\nUpcoming Deadlines:\\n{deadline_summary}\\n\\nCritical (<=1 day): {has_critical}\\n\\nPlease ensure all filings are completed before their deadlines. Reply to acknowledge receipt of this reminder.",
      ),

      // 5. Slack alert
      slackNode(
        "5",
        "Slack: Tax deadline alert",
        1000,
        ":calendar: *Tax Filing Deadlines*\\n{deadline_count} deadlines approaching:\\n{deadline_summary}\\nCritical: {has_critical}",
      ),

      // 6. Missing ack escalation
      n("6", "delayAction", "Wait for acknowledgement (4h)", 1240, {
        delayDetails: { delayType: "fixed", delayValue: 4, delayUnit: "hours" },
      }),

      // 7. Filing status logging (Sheets)
      sheetsNode(
        "7",
        "Log filing status to Sheets",
        1480,
        "TaxDeadlineLog",
        '[["{deadline_count}","{has_critical}","{has_high_urgency}","{deadline_summary}","reminder_sent","' +
          new Date().toISOString() +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // high urgency -> email + slack
      e("3", "7", "output_2"), // low urgency -> log only
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 685. TAX FILING REMINDER (7 nodes)
  // ===================================================================

  "Tax Filing Reminder": {
    summary:
      "Structured tax filing reminder system with channel-preference routing and escalation. A filing schedule trigger pulls forms/preparers data, routes by channel preference, sends filing-specific reminders via email or Slack, escalates on approaching deadlines via condition, records completion to Sheets, and handles revised deadlines.",
    nodes: [
      trigger("Schedule: Tax filing schedule", "schedule", {
        schedule: "0 8 * * 1",
        howItStarts:
          "Runs weekly on Mondays at 8 AM to check upcoming tax filing obligations and send targeted reminders",
        setup:
          "Set weekly schedule (Monday 8 AM). Create FilingTracker Google Sheet with columns: Filing, Form, Preparer, PreparerEmail, Channel, Deadline, Status.",
        inputFields: [],
      }),

      // 2. Forms/preparers pull (runCode)
      n("2", "runCode", "Pull filing forms & preparer assignments", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Simulates pulling from a filing tracker database/sheet
const filings = [
  { filing: "Federal Income Tax", form: "1120", preparer: "Tax Manager", email: "tax.manager@company.com", channel: "email", deadline: "2025-04-15", status: "in_progress" },
  { filing: "State Sales Tax", form: "ST-100", preparer: "AP Lead", email: "ap.lead@company.com", channel: "slack", deadline: "2025-04-20", status: "not_started" },
  { filing: "Payroll Tax", form: "941", preparer: "Payroll Mgr", email: "payroll@company.com", channel: "email", deadline: "2025-04-30", status: "in_progress" },
];
const now = new Date();
const upcoming = filings.filter(f => {
  const dl = new Date(f.deadline);
  const days = Math.floor((dl.getTime() - now.getTime()) / 86400000);
  return days <= 30 && days >= 0 && f.status !== "completed";
});
const filingSummary = upcoming.map(f => f.filing + " (" + f.form + ") — " + f.preparer + " — " + f.deadline + " [" + f.status + "]").join("\\n");
const primaryChannel = upcoming[0]?.channel || "email";
const primaryEmail = upcoming[0]?.email || "tax_team@company.com";
const hasEscalation = upcoming.some(f => { const d = Math.floor((new Date(f.deadline).getTime() - now.getTime()) / 86400000); return d <= 7; }) ? "yes" : "no";
return { filing_summary: filingSummary, filing_count: String(upcoming.length), primary_channel: primaryChannel, primary_email: primaryEmail, has_escalation: hasEscalation, filings_json: JSON.stringify(upcoming) };`,
          outputVariables: [
            { name: "filing_summary", type: "string" },
            { name: "filing_count", type: "string" },
            { name: "primary_channel", type: "string" },
            { name: "primary_email", type: "string" },
            { name: "has_escalation", type: "string" },
            { name: "filings_json", type: "string" },
          ],
        },
      }),

      // 3. Channel preference routing
      n("3", "condition", "Preferred channel is Slack?", 520, {
        conditionData: [
          {
            variable: "primary_channel",
            value: "slack",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Filing-specific reminder (email)
      emailNode(
        "4",
        "Email: Filing reminder",
        760,
        "{primary_email}",
        "Tax Filing Reminder: {filing_count} filings due",
        "Weekly Tax Filing Reminder\\n\\nUpcoming Filings:\\n{filing_summary}\\n\\nTotal: {filing_count} filings\\nEscalation needed: {has_escalation}\\n\\nPlease update the filing tracker with your progress. Contact the tax department for questions.",
        300,
      ),

      // 5. Filing-specific reminder (Slack)
      slackNode(
        "5",
        "Slack: Filing reminder",
        760,
        ":memo: *Weekly Tax Filing Reminder*\\n{filing_count} filings due:\\n{filing_summary}\\nEscalation: {has_escalation}",
        520,
      ),

      // 6. Deadline escalation condition + completion recording
      n("6", "condition", "Deadline within 7 days (escalate)?", 1000, {
        conditionData: [
          {
            variable: "has_escalation",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Completion recording (Sheets) + revised deadline handling
      sheetsNode(
        "7",
        "Log filing status to Sheets",
        1240,
        "FilingStatusLog",
        '[["{filing_count}","{filing_summary}","{primary_channel}","{has_escalation}","reminder_sent","' +
          new Date().toISOString() +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "5", "output_1"), // Slack channel -> Slack reminder
      e("3", "4", "output_2"), // email channel -> email reminder
      e("4", "6"),
      e("5", "6"),
      e("6", "7", "output_1"), // escalation -> log with escalation flag
      e("6", "7", "output_2"), // no escalation -> normal log
    ],
  },

  // ===================================================================
  // 686. VENDOR INVOICE DUPLICATE DETECTOR (8 nodes)
  // ===================================================================

  "Vendor Invoice Duplicate Detector": {
    summary:
      "AI-powered vendor invoice duplicate detection with fuzzy matching. An invoice ingestion trigger extracts fields via AI, performs fuzzy duplicate search via runCode, routes by confidence via condition, holds payment for likely duplicates, sends an AP review Slack alert, logs false positives to Sheets, and archives confirmed duplicates.",
    nodes: [
      trigger("Webhook: Invoice ingested", "webhook_app", {
        source: "AP System / Email",
        event: "invoice.received",
        howItStarts:
          "Fires when a new vendor invoice is received via AP system or email, triggering duplicate detection",
        setup:
          "Configure webhook for invoice ingestion events. Ensure AI extraction and Slack are connected. Create DuplicateLog and InvoiceArchive Google Sheets.",
        inputFields: [],
      }),

      // 2. Field extraction (AI)
      aiNode(
        "2",
        "AI: Extract invoice fields",
        280,
        "You are an accounts payable data extraction specialist. Parse the invoice data to extract all key fields regardless of format. Respond JSON: {vendor_name: string, vendor_id: string, invoice_number: string, invoice_date: string, due_date: string, total_amount: string, currency: string, line_items: string, po_number: string, ap_email: string}",
        "Invoice data: {webhook_payload}",
        [
          { field: "vendor_name", as: "vendor_name", type: "string" },
          { field: "vendor_id", as: "vendor_id", type: "string" },
          { field: "invoice_number", as: "invoice_number", type: "string" },
          { field: "invoice_date", as: "invoice_date", type: "string" },
          { field: "due_date", as: "due_date", type: "string" },
          { field: "total_amount", as: "total_amount", type: "string" },
          { field: "currency", as: "currency", type: "string" },
          { field: "line_items", as: "line_items", type: "string" },
          { field: "po_number", as: "po_number", type: "string" },
          { field: "ap_email", as: "ap_email", type: "string" },
        ],
      ),

      // 3. Fuzzy duplicate search (runCode)
      n("3", "runCode", "Fuzzy duplicate search", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Simulates fuzzy matching against existing invoices
// In production, this queries the invoice database
const vendorName = vars.vendor_name || "";
const invoiceNum = vars.invoice_number || "";
const amount = vars.total_amount || "0";
const date = vars.invoice_date || "";

// Fuzzy matching heuristics
let duplicateConfidence = 0;
let matchDetails = "No matches found";
let matchedInvoiceId = "";

// Check exact invoice number match for same vendor
if (invoiceNum && vendorName) {
  // Simulate: exact match would score 95+
  duplicateConfidence = 10; // Default low
  matchDetails = "Invoice " + invoiceNum + " from " + vendorName + " ($" + amount + ") — no exact duplicates found";
}

// In production, actual DB query results would set these
const confidenceLevel = duplicateConfidence >= 80 ? "high" : duplicateConfidence >= 50 ? "medium" : "low";
const shouldHold = confidenceLevel === "high" || confidenceLevel === "medium" ? "yes" : "no";
return { duplicate_confidence: String(duplicateConfidence), confidence_level: confidenceLevel, should_hold: shouldHold, match_details: matchDetails, matched_invoice_id: matchedInvoiceId };`,
          outputVariables: [
            { name: "duplicate_confidence", type: "string" },
            { name: "confidence_level", type: "string" },
            { name: "should_hold", type: "string" },
            { name: "match_details", type: "string" },
            { name: "matched_invoice_id", type: "string" },
          ],
        },
      }),

      // 4. Confidence condition
      n("4", "condition", "Likely duplicate (medium/high confidence)?", 760, {
        conditionData: [
          {
            variable: "should_hold",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Payment hold for likely duplicates
      slackNode(
        "5",
        "Slack: Payment hold — probable duplicate",
        1000,
        ":no_entry: *PAYMENT HOLD — Probable Duplicate Invoice*\\nVendor: {vendor_name}\\nInvoice: {invoice_number}\\nAmount: ${total_amount} {currency}\\nDate: {invoice_date}\\nConfidence: {duplicate_confidence}% ({confidence_level})\\nDetails: {match_details}\\nPO: {po_number}\\n\\nPlease review and approve or reject in the AP system.",
      ),

      // 6. AP review Slack alert
      slackNode(
        "6",
        "Slack: New invoice processed",
        1240,
        ":receipt: *New Invoice Processed*\\nVendor: {vendor_name}\\nInvoice: {invoice_number}\\nAmount: ${total_amount} {currency}\\nDue: {due_date}\\nDuplicate check: {confidence_level} ({duplicate_confidence}%)\\nPO: {po_number}",
      ),

      // 7. False-positive logging (Sheets)
      sheetsNode(
        "7",
        "Log duplicate check to Sheets",
        1480,
        "DuplicateLog",
        '[["{vendor_name}","{invoice_number}","{total_amount}","{currency}","{invoice_date}","{duplicate_confidence}","{confidence_level}","{should_hold}","{match_details}","' +
          new Date().toISOString() +
          '"]]',
      ),

      // 8. Confirmed duplicate archival
      sheetsNode(
        "8",
        "Archive invoice record",
        1720,
        "InvoiceArchive",
        '[["{vendor_name}","{vendor_id}","{invoice_number}","{total_amount}","{currency}","{due_date}","{po_number}","{confidence_level}","' +
          new Date().toISOString() +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // likely duplicate -> hold
      e("4", "6", "output_2"), // not duplicate -> normal processing
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 687. VENDOR INVOICE PROCESSOR (10 nodes)
  // ===================================================================

  "Vendor Invoice Processor": {
    summary:
      "End-to-end vendor invoice processing with three-way matching and approval routing. An invoice email trigger extracts data via AI, validates the vendor, matches the PO, performs three-way matching via runCode, routes by match result via condition, handles approval routing, posts to QuickBooks, notifies vendor/requester, and logs to AP Sheets.",
    nodes: [
      trigger("Webhook: Invoice email received", "email", {
        howItStarts:
          "An invoice email is received in the AP inbox, triggering automated processing and three-way matching",
        setup:
          "Configure email trigger for the AP inbox. Ensure vendor database, PO system, QuickBooks, and Slack are connected.",
        inputFields: [],
      }),

      // 2. AI data extraction
      aiNode(
        "2",
        "AI: Extract invoice data",
        280,
        "You are an AP automation specialist. Parse the invoice email to extract all billing data regardless of format: vendor name, invoice number, date, line items with quantities and prices, totals, payment terms, PO reference, and bank details. Respond JSON: {vendor_name: string, vendor_id: string, invoice_number: string, invoice_date: string, due_date: string, line_items_json: string, subtotal: string, tax: string, total: string, currency: string, po_number: string, payment_terms: string, vendor_email: string, requester_email: string}",
        "Invoice email: {webhook_payload}",
        [
          { field: "vendor_name", as: "vendor_name", type: "string" },
          { field: "vendor_id", as: "vendor_id", type: "string" },
          { field: "invoice_number", as: "invoice_number", type: "string" },
          { field: "invoice_date", as: "invoice_date", type: "string" },
          { field: "due_date", as: "due_date", type: "string" },
          { field: "line_items_json", as: "line_items_json", type: "string" },
          { field: "subtotal", as: "subtotal", type: "string" },
          { field: "tax", as: "tax_amount", type: "string" },
          { field: "total", as: "total_amount", type: "string" },
          { field: "currency", as: "currency", type: "string" },
          { field: "po_number", as: "po_number", type: "string" },
          { field: "payment_terms", as: "payment_terms", type: "string" },
          { field: "vendor_email", as: "vendor_email", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
        ],
      ),

      // 3. Vendor validation
      n("3", "hitApi", "Validate vendor in vendor database", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.yourvendorsystem.com/v1/vendors/{vendor_id}",
          headers: [
            { key: "Authorization", value: "Bearer {vendor_system_api_key}" },
            { key: "Accept", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "status", customName: "vendor_status", type: "string" },
            {
              name: "payment_method",
              customName: "vendor_payment_method",
              type: "string",
            },
            {
              name: "approved",
              customName: "vendor_approved",
              type: "boolean",
            },
          ],
        },
      }),

      // 4. PO matching
      n("4", "hitApi", "Match purchase order", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.yourvendorsystem.com/v1/purchase-orders/{po_number}",
          headers: [
            { key: "Authorization", value: "Bearer {vendor_system_api_key}" },
            { key: "Accept", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "po_total", customName: "po_total", type: "string" },
            {
              name: "po_line_items",
              customName: "po_line_items_json",
              type: "string",
            },
            { name: "po_status", customName: "po_status", type: "string" },
            {
              name: "receiving_total",
              customName: "receiving_total",
              type: "string",
            },
          ],
        },
      }),

      // 5. Three-way match (runCode)
      n("5", "runCode", "Three-way match: PO vs Invoice vs Receipt", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `const invoiceTotal = parseFloat(vars.total_amount) || 0;
const poTotal = parseFloat(vars.po_total) || 0;
const receivingTotal = parseFloat(vars.receiving_total) || 0;
const tolerance = 0.02; // 2% tolerance

const poMatch = poTotal > 0 ? Math.abs(invoiceTotal - poTotal) / poTotal <= tolerance : false;
const receiptMatch = receivingTotal > 0 ? Math.abs(invoiceTotal - receivingTotal) / receivingTotal <= tolerance : false;
const threeWayMatch = poMatch && receiptMatch;

const matchStatus = threeWayMatch ? "full_match" : poMatch ? "po_match_only" : receiptMatch ? "receipt_match_only" : "no_match";
const autoApprove = threeWayMatch && invoiceTotal <= 5000 ? "yes" : "no";
const varianceAmount = Math.abs(invoiceTotal - poTotal).toFixed(2);
const variancePercent = poTotal > 0 ? ((Math.abs(invoiceTotal - poTotal) / poTotal) * 100).toFixed(1) : "N/A";
const approverEmail = invoiceTotal > 5000 ? "cfo@company.com" : "ap.manager@company.com";
return { match_status: matchStatus, auto_approve: autoApprove, variance_amount: varianceAmount, variance_percent: variancePercent, three_way_match: threeWayMatch ? "yes" : "no", approver_email: approverEmail };`,
          outputVariables: [
            { name: "match_status", type: "string" },
            { name: "auto_approve", type: "string" },
            { name: "variance_amount", type: "string" },
            { name: "variance_percent", type: "string" },
            { name: "three_way_match", type: "string" },
            { name: "approver_email", type: "string" },
          ],
        },
      }),

      // 6. Match condition
      n("6", "condition", "Three-way match passed?", 1240, {
        conditionData: [
          {
            variable: "three_way_match",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Approval routing
      emailNode(
        "7",
        "Email: Invoice approval request",
        1480,
        "{approver_email}",
        "Invoice Approval: {vendor_name} #{invoice_number} — ${total_amount}",
        "Invoice requires approval:\\n\\nVendor: {vendor_name}\\nInvoice: #{invoice_number}\\nAmount: ${total_amount} {currency}\\nPO: {po_number}\\n\\nMatch Status: {match_status}\\nVariance: ${variance_amount} ({variance_percent}%)\\nAuto-approve eligible: {auto_approve}\\n\\nPlease review and approve in the AP system.",
      ),

      // 8. QuickBooks posting
      quickbooksNode(
        "8",
        "Post to QuickBooks",
        1720,
        "POST",
        "bill",
        [
          { key: "VendorRef", value: '{"value":"{vendor_id}"}' },
          { key: "TxnDate", value: "{invoice_date}" },
          { key: "DueDate", value: "{due_date}" },
          { key: "TotalAmt", value: "{total_amount}" },
          { key: "DocNumber", value: "{invoice_number}" },
          { key: "Line", value: "{line_items_json}" },
        ],
        [
          { name: "Bill.Id", customName: "qb_bill_id", type: "string" },
          {
            name: "Bill.Balance",
            customName: "qb_bill_balance",
            type: "string",
          },
        ],
      ),

      // 9. Vendor/requester notification
      emailNode(
        "9",
        "Email: Processing confirmation",
        1960,
        "{vendor_email}",
        "Invoice #{invoice_number} received and processing — {vendor_name}",
        "Dear {vendor_name},\\n\\nWe have received and are processing your invoice #{invoice_number} for ${total_amount} {currency}.\\n\\nPO Reference: {po_number}\\nDue Date: {due_date}\\nPayment Terms: {payment_terms}\\nMatch Status: {match_status}\\n\\nYou will be notified when payment is scheduled.",
      ),

      // 10. AP logging (Sheets)
      sheetsNode(
        "10",
        "Log to AP ledger",
        2200,
        "APLedger",
        '[["{vendor_name}","{invoice_number}","{total_amount}","{currency}","{invoice_date}","{due_date}","{po_number}","{match_status}","{three_way_match}","{qb_bill_id}","' +
          new Date().toISOString() +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "8", "output_1"), // match -> post to QB
      e("6", "7", "output_2"), // no match -> approval required
      e("7", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ===================================================================
  // 688. VENDOR PAYMENT REMINDER (7 nodes)
  // ===================================================================

  "Vendor Payment Reminder": {
    summary:
      "Daily AP scan with criticality-based vendor payment reminders. A daily schedule retrieves due payments, classifies criticality via runCode, emails the AP queue, posts a weekly Slack payment calendar, checks cash sufficiency via condition, and sends a treasury alert when funds are low.",
    nodes: [
      trigger("Schedule: Daily AP scan", "schedule", {
        schedule: "0 8 * * *",
        howItStarts:
          "Runs daily at 8 AM to scan accounts payable for due and upcoming vendor payments",
        setup:
          "Set daily schedule at 8 AM. Connect your AP system or QuickBooks for payment data. Create PaymentCalendar Google Sheet.",
        inputFields: [],
      }),

      // 2. Due payment retrieval
      quickbooksNode(
        "2",
        "Pull due vendor payments",
        280,
        "GET",
        "query?query=SELECT * FROM Bill WHERE DueDate <= '2025-04-30' AND Balance > '0'",
        [],
        [
          {
            name: "QueryResponse.Bill",
            customName: "due_bills_json",
            type: "string",
          },
          {
            name: "QueryResponse.totalCount",
            customName: "due_bill_count",
            type: "number",
          },
        ],
      ),

      // 3. Criticality classification (runCode)
      n("3", "runCode", "Classify payment criticality", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `const bills = JSON.parse(vars.due_bills_json || "[]");
const now = new Date();
let totalDue = 0;
const payments = [];
for (const bill of bills) {
  const dueDate = new Date(bill.DueDate || "");
  const daysUntilDue = Math.floor((dueDate.getTime() - now.getTime()) / 86400000);
  const amount = parseFloat(bill.Balance || bill.TotalAmt || 0);
  totalDue += amount;
  let criticality = "low";
  if (daysUntilDue < 0) criticality = "overdue";
  else if (daysUntilDue <= 3) criticality = "critical";
  else if (daysUntilDue <= 7) criticality = "high";
  else if (daysUntilDue <= 14) criticality = "medium";
  payments.push({ vendor: bill.VendorRef?.name || "Unknown", amount: amount.toFixed(2), due: bill.DueDate, days: daysUntilDue, criticality, bill_id: bill.Id });
}
payments.sort((a, b) => a.days - b.days);
const paymentSummary = payments.map(p => p.vendor + " — $" + p.amount + " due " + p.due + " (" + p.criticality + ")").join("\\n");
const hasOverdue = payments.some(p => p.criticality === "overdue") ? "yes" : "no";
const cashNeeded = totalDue.toFixed(2);
const apEmail = "ap@company.com";
const treasuryEmail = "treasury@company.com";
return { payment_summary: paymentSummary, payment_count: String(payments.length), total_due: cashNeeded, has_overdue: hasOverdue, ap_email: apEmail, treasury_email: treasuryEmail };`,
          outputVariables: [
            { name: "payment_summary", type: "string" },
            { name: "payment_count", type: "string" },
            { name: "total_due", type: "string" },
            { name: "has_overdue", type: "string" },
            { name: "ap_email", type: "string" },
            { name: "treasury_email", type: "string" },
          ],
        },
      }),

      // 4. AP queue email
      emailNode(
        "4",
        "Email: Daily AP payment queue",
        760,
        "{ap_email}",
        "AP Payment Queue: {payment_count} payments due — ${total_due} total",
        "Daily Accounts Payable Report\\n\\nPayments Due:\\n{payment_summary}\\n\\nTotal Due: ${total_due}\\nOverdue items: {has_overdue}\\n\\nPlease process overdue and critical payments immediately.",
      ),

      // 5. Weekly Slack calendar
      slackNode(
        "5",
        "Slack: Payment calendar",
        1000,
        ":money_with_wings: *AP Payment Queue*\\n{payment_count} payments due (${total_due} total)\\nOverdue: {has_overdue}\\n\\n{payment_summary}",
      ),

      // 6. Cash sufficiency condition
      n("6", "condition", "Sufficient cash for all payments?", 1240, {
        conditionData: [
          {
            variable: "has_overdue",
            value: "no",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Treasury alert
      emailNode(
        "7",
        "Email: Treasury cash alert",
        1480,
        "{treasury_email}",
        "URGENT: Overdue vendor payments — ${total_due} needed",
        "Treasury Alert\\n\\nThere are overdue vendor payments requiring immediate attention.\\n\\nTotal Due: ${total_due}\\nPayment Count: {payment_count}\\nOverdue: {has_overdue}\\n\\nDetails:\\n{payment_summary}\\n\\nPlease ensure sufficient funds are available and prioritize overdue payments.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "5", "output_1"), // sufficient -> done (terminal at Slack)
      e("6", "7", "output_2"), // overdue/insufficient -> treasury alert
    ],
  },

  // ===================================================================
  // 689. WHATSAPP PAYMENT REMINDER (8 nodes)
  // ===================================================================

  "WhatsApp Payment Reminder": {
    summary:
      "WhatsApp-first payment reminder flow with opt-in validation and email fallback. An invoice scan checks WhatsApp eligibility, validates opt-in via condition, selects tone by due stage, sends a WhatsApp message with payment link, falls back to email on delivery failure, monitors payment clearance via condition, and logs time-to-payment to Sheets.",
    nodes: [
      trigger("Schedule: Daily invoice scan", "schedule", {
        schedule: "0 9 * * *",
        howItStarts:
          "Runs daily at 9 AM to scan outstanding invoices and send WhatsApp payment reminders to eligible customers",
        setup:
          "Set daily schedule at 9 AM. Configure Twilio WhatsApp Business API. Create PaymentReminders Google Sheet. Ensure customer WhatsApp consent data is available.",
        inputFields: [],
      }),

      // 2. Customer WhatsApp eligibility check (runCode)
      n("2", "runCode", "Check WhatsApp eligibility & build reminder", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// In production, queries invoice + customer database
const invoices = [
  { customer: "Acme Corp", email: "billing@acme.com", phone: "+15551234567", wa_optin: true, amount: "2500.00", currency: "USD", invoice_id: "INV-001", due_date: "2025-04-10", issued: "2025-03-25" },
];
const now = new Date();
const reminder = invoices[0]; // First overdue
const dueDate = new Date(reminder.due_date);
const daysOverdue = Math.floor((now.getTime() - dueDate.getTime()) / 86400000);
const dueStage = daysOverdue > 30 ? "final" : daysOverdue > 14 ? "urgent" : daysOverdue > 0 ? "overdue" : "upcoming";
const toneMap = { upcoming: "friendly", overdue: "firm", urgent: "urgent", final: "final_notice" };
const tone = toneMap[dueStage] || "friendly";
const hasWhatsApp = reminder.wa_optin && reminder.phone ? "yes" : "no";
const paymentLink = "https://pay.example.com/inv/" + reminder.invoice_id;
return { customer_name: reminder.customer, customer_email: reminder.email, customer_phone: reminder.phone, wa_optin: hasWhatsApp, amount: reminder.amount, currency: reminder.currency, invoice_id: reminder.invoice_id, due_date: reminder.due_date, days_overdue: String(daysOverdue), due_stage: dueStage, reminder_tone: tone, payment_link: paymentLink };`,
          outputVariables: [
            { name: "customer_name", type: "string" },
            { name: "customer_email", type: "string" },
            { name: "customer_phone", type: "string" },
            { name: "wa_optin", type: "string" },
            { name: "amount", type: "string" },
            { name: "currency", type: "string" },
            { name: "invoice_id", type: "string" },
            { name: "due_date", type: "string" },
            { name: "days_overdue", type: "string" },
            { name: "due_stage", type: "string" },
            { name: "reminder_tone", type: "string" },
            { name: "payment_link", type: "string" },
          ],
        },
      }),

      // 3. Opt-in validation condition
      n("3", "condition", "Customer opted-in to WhatsApp?", 520, {
        conditionData: [
          {
            variable: "wa_optin",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Tone selection by due stage (AI)
      aiNode(
        "4",
        "AI: Craft tone-appropriate message",
        760,
        "You are a collections communications specialist. Craft a WhatsApp payment reminder matching the specified tone: friendly (upcoming), firm (overdue), urgent (late), or final_notice (very late). Keep it short, professional, and include the payment link. Respond JSON: {wa_message: string, subject_line: string, email_body: string}",
        "Customer: {customer_name}. Invoice: {invoice_id}. Amount: ${amount} {currency}. Due: {due_date}. Days overdue: {days_overdue}. Tone: {reminder_tone}. Payment link: {payment_link}.",
        [
          { field: "wa_message", as: "wa_message", type: "string" },
          { field: "subject_line", as: "email_subject", type: "string" },
          { field: "email_body", as: "email_body", type: "string" },
        ],
      ),

      // 5. WhatsApp send with payment link
      n("5", "whatsApp", "WhatsApp: Payment reminder", 1000, {
        whatsappDetails: {
          templateName: "payment_reminder",
          lang: "en",
          to: "{customer_phone}",
          smsBody: "{wa_message}",
        },
      }),

      // 6. Delivery fallback (email)
      emailNode(
        "6",
        "Email: Payment reminder fallback",
        1240,
        "{customer_email}",
        "{email_subject}",
        "{email_body}",
      ),

      // 7. Payment-cleared stop condition
      n("7", "condition", "WhatsApp delivered successfully?", 1480, {
        conditionData: [
          {
            variable: "wa_optin",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Time-to-payment logging (Sheets)
      sheetsNode(
        "8",
        "Log payment reminder to Sheets",
        1720,
        "PaymentReminders",
        '[["{customer_name}","{customer_email}","{customer_phone}","{invoice_id}","{amount}","{currency}","{due_date}","{days_overdue}","{due_stage}","{reminder_tone}","{wa_optin}","' +
          new Date().toISOString() +
          '"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // opted in -> craft message
      e("3", "6", "output_2"), // not opted in -> email fallback
      e("4", "5"),
      e("5", "7"),
      e("7", "8", "output_1"), // delivered -> log
      e("7", "6", "output_2"), // not delivered -> email fallback
      e("6", "8"),
    ],
  },

  // ===================================================================
  // 690. YEAR END FINANCIAL CHECKLIST (9 nodes)
  // ===================================================================

  "Year End Financial Checklist": {
    summary:
      "Comprehensive year-end financial close checklist with role assignment, progress tracking, and escalation. An annual trigger instantiates the checklist via runCode, assigns roles and deadlines, sends assignment notification emails, updates a progress dashboard in Sheets, sends weekly status emails, escalates overdue items via Slack, manages task dependencies via runCode, and posts a completion celebration message.",
    nodes: [
      trigger("Schedule: Annual year-end trigger", "schedule", {
        schedule: "0 9 1 12 *",
        howItStarts:
          "Triggers on December 1st at 9 AM to initiate the year-end financial close checklist for the upcoming fiscal year end",
        setup:
          "Set annual schedule (Dec 1, 9 AM). Create YearEndChecklist and ChecklistProgress Google Sheets. Ensure email and Slack are configured for all finance team members.",
        inputFields: [],
      }),

      // 2. Checklist instantiation (runCode)
      n("2", "runCode", "Instantiate checklist & assign roles", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `const year = new Date().getFullYear();
const now = new Date();
const checklist = [
  { task: "Reconcile all bank accounts", owner: "controller@company.com", role: "Controller", deadline: year + "-12-15", priority: "critical", depends_on: "" },
  { task: "Review accounts receivable aging", owner: "ar.manager@company.com", role: "AR Manager", deadline: year + "-12-18", priority: "high", depends_on: "" },
  { task: "Review accounts payable aging", owner: "ap.manager@company.com", role: "AP Manager", deadline: year + "-12-18", priority: "high", depends_on: "" },
  { task: "Inventory count & reconciliation", owner: "operations@company.com", role: "Operations", deadline: year + "-12-20", priority: "critical", depends_on: "" },
  { task: "Fixed asset depreciation", owner: "controller@company.com", role: "Controller", deadline: year + "-12-22", priority: "high", depends_on: "Reconcile all bank accounts" },
  { task: "Accrue unpaid expenses", owner: "ap.manager@company.com", role: "AP Manager", deadline: year + "-12-26", priority: "high", depends_on: "Review accounts payable aging" },
  { task: "Revenue recognition review", owner: "cfo@company.com", role: "CFO", deadline: year + "-12-28", priority: "critical", depends_on: "Review accounts receivable aging" },
  { task: "Tax provision calculation", owner: "tax.manager@company.com", role: "Tax Manager", deadline: year + "-12-30", priority: "critical", depends_on: "Revenue recognition review" },
  { task: "Intercompany eliminations", owner: "controller@company.com", role: "Controller", deadline: year + "-12-30", priority: "high", depends_on: "Reconcile all bank accounts" },
  { task: "Final trial balance review", owner: "cfo@company.com", role: "CFO", deadline: year + "-12-31", priority: "critical", depends_on: "Tax provision calculation" },
];
const assignments = checklist.map(t => {
  const dl = new Date(t.deadline);
  const daysLeft = Math.floor((dl.getTime() - now.getTime()) / 86400000);
  return { ...t, days_left: daysLeft, status: daysLeft < 0 ? "overdue" : daysLeft <= 3 ? "due_soon" : "on_track" };
});
const totalTasks = checklist.length;
const criticalTasks = checklist.filter(t => t.priority === "critical").length;
const uniqueOwners = [...new Set(checklist.map(t => t.owner))];
const ownerEmails = uniqueOwners.join(",");
const checklistSummary = checklist.map(t => t.task + " — " + t.role + " by " + t.deadline + " [" + t.priority + "]").join("\\n");
const overdue = assignments.filter(a => a.status === "overdue");
const dueSoon = assignments.filter(a => a.status === "due_soon");
const hasOverdue = overdue.length > 0 ? "yes" : "no";
const statusSummary = "On track: " + assignments.filter(a => a.status === "on_track").length + " | Due soon: " + dueSoon.length + " | Overdue: " + overdue.length;
return { checklist_json: JSON.stringify(checklist), assignments_json: JSON.stringify(assignments), total_tasks: String(totalTasks), critical_tasks: String(criticalTasks), owner_emails: ownerEmails, checklist_summary: checklistSummary, fiscal_year: String(year), has_overdue: hasOverdue, status_summary: statusSummary, overdue_count: String(overdue.length), due_soon_count: String(dueSoon.length) };`,
          outputVariables: [
            { name: "checklist_json", type: "string" },
            { name: "assignments_json", type: "string" },
            { name: "total_tasks", type: "string" },
            { name: "critical_tasks", type: "string" },
            { name: "owner_emails", type: "string" },
            { name: "checklist_summary", type: "string" },
            { name: "fiscal_year", type: "string" },
            { name: "has_overdue", type: "string" },
            { name: "status_summary", type: "string" },
            { name: "overdue_count", type: "string" },
            { name: "due_soon_count", type: "string" },
          ],
        },
      }),

      // 3. Assignment notifications (email)
      emailNode(
        "3",
        "Email: Year-end checklist assignments",
        520,
        "{owner_emails}",
        "Year-End Financial Close Checklist FY{fiscal_year} — Your Assignments",
        "Team,\\n\\nThe year-end financial close process has been initiated for FY{fiscal_year}.\\n\\nChecklist Overview:\\n{checklist_summary}\\n\\nTotal Tasks: {total_tasks}\\nCritical Tasks: {critical_tasks}\\nStatus: {status_summary}\\n\\nPlease review your assignments and begin work immediately. Update the progress dashboard as you complete each task.",
      ),

      // 4. Progress dashboard (Sheets)
      sheetsNode(
        "4",
        "Initialize progress dashboard",
        760,
        "ChecklistProgress",
        '[["{fiscal_year}","{total_tasks}","{critical_tasks}","{status_summary}","{overdue_count}","{due_soon_count}","initialized","' +
          new Date().toISOString() +
          '"]]',
      ),

      // 5. Weekly status email
      emailNode(
        "5",
        "Email: Weekly status update",
        1000,
        "{owner_emails}",
        "Year-End Close Status: {status_summary} — FY{fiscal_year}",
        "Weekly Year-End Close Status\\n\\nFiscal Year: {fiscal_year}\\nStatus: {status_summary}\\n\\nOverdue: {overdue_count} tasks\\nDue Soon: {due_soon_count} tasks\\n\\nFull Checklist:\\n{checklist_summary}\\n\\nPlease prioritize overdue and due-soon items. Contact the Controller for blockers.",
      ),

      // 6. Overdue Slack escalation
      slackNode(
        "6",
        "Slack: Overdue item escalation",
        1240,
        ":rotating_light: *Year-End Close — Overdue Items*\\nFY{fiscal_year} Close Checklist\\n{status_summary}\\nOverdue tasks: {overdue_count}\\nDue soon: {due_soon_count}\\n\\nImmediate attention required from assigned owners.",
      ),

      // 7. Dependency management (runCode)
      n("7", "runCode", "Check dependency chain status", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `const assignments = JSON.parse(vars.assignments_json || "[]");
const completed = assignments.filter(a => a.status === "completed").map(a => a.task);
const blocked = [];
for (const a of assignments) {
  if (a.depends_on && !completed.includes(a.depends_on) && a.status !== "completed") {
    blocked.push(a.task + " (blocked by: " + a.depends_on + ")");
  }
}
const blockedCount = blocked.length;
const blockedSummary = blocked.length > 0 ? blocked.join("\\n") : "No blocked tasks";
const completedCount = completed.length;
const completionPercent = assignments.length > 0 ? ((completedCount / assignments.length) * 100).toFixed(0) : "0";
const allComplete = completedCount === assignments.length ? "yes" : "no";
return { blocked_count: String(blockedCount), blocked_summary: blockedSummary, completed_count: String(completedCount), completion_percent: completionPercent, all_complete: allComplete };`,
          outputVariables: [
            { name: "blocked_count", type: "string" },
            { name: "blocked_summary", type: "string" },
            { name: "completed_count", type: "string" },
            { name: "completion_percent", type: "string" },
            { name: "all_complete", type: "string" },
          ],
        },
      }),

      // 8. Dependency status update (Sheets)
      sheetsNode(
        "8",
        "Update dependency status in Sheets",
        1720,
        "ChecklistProgress",
        '[["{fiscal_year}","{completed_count}","{total_tasks}","{completion_percent}%","{blocked_count}","{blocked_summary}","{status_summary}","' +
          new Date().toISOString() +
          '"]]',
      ),

      // 9. Completion celebration
      slackNode(
        "9",
        "Slack: Year-end close progress & celebration",
        1960,
        ":chart_with_upwards_trend: *Year-End Close Progress — FY{fiscal_year}*\\nCompleted: {completed_count}/{total_tasks} ({completion_percent}%)\\nBlocked: {blocked_count}\\n{blocked_summary}\\n\\nKeep up the great work, team!",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },
};
